import React, {
  useMemo,
  useRef,
  useState,
} from "react"

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"

import {
  ClockIcon,
  SunIcon,
  WrenchIcon,
  UserGroupIcon,    // 21 Aug 2026
} from "@heroicons/react/24/solid"

import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts"

import { userProfileIcon2 } from "@/assets/img/imgAssets"
import { getTimeSince } from "@/utils/EtGetTimeSince"

import EtChallengeIcon1SVGComp from "@/assets/img/icons/svgComp/EtChallengeIcon1SVGComp"
import EtModalAddOrUpdateUserChallengeSelfEval from "@/components/common/modal/EtModalAddOrUpdateUserChallengeSelfEval"

import { useGetUserPicQuery } from "../users/usersApiSlice"
import { useGetUserChallengeSelfEvalsQuery } from "../userChallengeSelfEvals/userChallengeSelfEvalsApiSlice"
import { useGetUserChallengeCoachEvalsQuery } from "../userChallengeCoachEvals/userChallengeCoachEvalsApiSlice"
import { useGetUserChallengeSponsorEvalsQuery } from "../userChallengeSponsorEvals/userChallengeSponsorEvalsApiSlice"

// 21 Aug 2026 - Team evaluations
import { useGetUserChallengeTeamEvalsQuery } from "../userChallengeTeamEvals/userChallengeTeamEvalsApiSlice"

// 04 Aug 2026
import EtIndividualEvaluationReportPreview from "../reporting/EtIndividualEvaluationReportPreview"

import {
  useDownloadIndividualChallengeEvaluationPowerPointMutation,
  // 08 Aug 2026
  useDownloadIndividualChallengeEvaluationPdfMutation,
  // 06 Aug 2026
  useDownloadMultiChallengeEvaluationPowerPointMutation,
  // 08 Aug 2026
  useDownloadMultiChallengeEvaluationPdfMutation,
  useEmailIndividualChallengeEvaluationPdfMutation,
  useEmailMultiChallengeEvaluationPdfMutation,  

  useEmailIndividualChallengeEvaluationInlineMutation,
  useEmailMultiChallengeEvaluationInlineMutation,

  // useEmailIndividualChallengeEvaluationReportMutation,
  // useEmailMultiChallengeEvaluationReportMutation,      
} from "../reporting/evaluationReportsApiSlice"

// 16 Aug 2026 - evaluation report access controls
import useAuth from '@/hooks/useAuth'

const radarColors = [
  "#ea580c",
  "#65a30d",
  "#2563eb",
  "#9333ea",
  "#0891b2",
  "#db2777",
  "#ca8a04",
  "#4f46e5",
]

// 21 Aug 2026 - Build one Team aggregate for a challenge dimension.
//
// Team evaluations remain individually attributable records in storage.
// The user's primary evaluation view presents the average Team perspective.
//
// This helper is kept at module scope because it is used by both
// the radar configuration and the Lumin evaluation helpers.
const getTeamDimensionAggregate = (
  teamEvaluations = [],
  dimensionId
) => {

  // Normalize populated and non populated Mongo references locally.
  const getId = (
    value
  ) => {
    return (
      value?._id?.toString() ||
      value?.toString() ||
      ""
    )
  }


  // Normalize score locally because normalizeEvalScore
  // belongs to EtUserSessionChallengesSection scope.
  const normalizeScore = (
    score
  ) => {

    if (
      score === undefined ||
      score === null ||
      score === ""
    ) {
      return null
    }

    const numericScore =
      Number(
        score
      )

    return Number.isFinite(
      numericScore
    )
      ? numericScore
      : null
  }


  const matchingDimensionEvaluations =
    teamEvaluations
      .map(
        (
          teamEvaluation
        ) => {

          return teamEvaluation
            ?.userChallengeTeamEvals
            ?.find(
              (
                evaluation
              ) =>
                getId(
                  evaluation
                    ?.userChallengeDimId
                ) ===
                getId(
                  dimensionId
                )
            )
        }
      )
      .filter(
        Boolean
      )


  const scores =
    matchingDimensionEvaluations
      .map(
        (
          evaluation
        ) =>
          normalizeScore(
            evaluation
              ?.userChallengeDimTeamScore
          )
      )
      .filter(
        (
          score
        ) =>
          score !== null
      )


  if (
    !scores.length
  ) {
    return {
      score: null,
      evaluationCount: 0,
      commentCount: 0,
    }
  }


  const averageScore =
    scores.reduce(
      (
        sum,
        score
      ) =>
        sum + score,
      0
    ) /
    scores.length


  const commentCount =
    matchingDimensionEvaluations
      .filter(
        (
          evaluation
        ) =>
          evaluation
            ?.userChallengeDimTeamEvalText
            ?.trim()
      )
      .length


  return {
    score:
      Number(
        averageScore.toFixed(
          1
        )
      ),

    evaluationCount:
      scores.length,

    commentCount,
  }
}


const EtUserSessionChallengesSection = ({
  sessionChallenges = [],
  userId,
  trailId,
  sessionId,
  userOrgId,
  selfUserData,
  cohortId,
}) => {

  // First Created - 21 Aug 2024
  // Author - Atul Pandey
  // Purpose - to simplify and decouple the Session Challenges section JSX from the main session display component
  // Updated - 23 Aug 2024 - added triggeringUrl to capture and pass the URL triggering the modal
  // Updated - 23 Aug 2024 - added user challenge self evaluation scores
  // Updated - 23 Aug 2024 - added Et Lumin colors to depict evaluation scores
  // Updated - 24 Aug 2024 - added evaluation headers for dimensions and evaluators
  // Updated - 24 Aug 2024 - added sponsor evaluations
  // Updated - 25 Aug 2024 - added sponsor user challenge evaluations
  // Updated - 28 Aug 2024 - refined display of coach, sponsor and self evaluation data
  // Updated - 29 Aug 2024 - refined coach and sponsor evaluations using memoized data
  // Updated - 30 Aug 2024 - added user, coach and sponsor evaluation comments
  // Updated - 31 Aug 2024 - added modal to create or update user challenge self evaluations
  // Updated - 14 Sep 2024 - refined the header JSX from flex to grid
  // Updated - 04 Jul 2026 - added cohortId for session challenge evaluations
  // Updated - 30 Jul 2026 - added Lumin View and Spider View for multi dimensional evaluation display
  // Updated - 01 Aug 2026 - extracted as a reusable component for all UserTrailSession<SessionType>CompProd components
  // Updated - 01 Aug 2026 - added normalized ID handling for populated and non populated references
  // Updated - 01 Aug 2026 - moved the evaluation modal outside the challenge map to maintain one modal instance
  // Updated - 01 Aug 2026 - added challenge specific state keys to avoid collisions across multiple challenges
  // Updated - 01 Aug 2026 - changed the dimension and evaluator columns to one aligned CSS grid
  // Updated - 01 Aug 2026 - added a sticky dimension column and horizontally scrollable evaluator matrix
  // Updated - 01 Aug 2026 - added evaluator specific inline evaluation comments
  // Updated - 01 Aug 2026 - added show and hide all comments control for each dimension
  // Updated - 01 Aug 2026 - added stacked mobile dimension cards with evaluator specific comments
  // Updated - 02 Aug 2026 - dimensions updated for newly created eval
  // Updated - 02 Aug 2026 - changes for multiple evals
  // Updated - 02 Aug 2026 - rebuilt evaluation history around exact evaluation number snapshots
  // Updated - 02 Aug 2026 - stopped carrying earlier coach or sponsor evaluations into later self evaluations
  // Updated - 02 Aug 2026 - added a challenge level Trend carousel with one spider card per self evaluation
  // Updated - 02 Aug 2026 - added independent Lumin and Spider views within every evaluation history card
  // Updated - 03 Aug 2026 - moved Instructions above evaluation history
  // Updated - 03 Aug 2026 - replaced collapsible history cards with Trend and Eval tabs
  // Updated - 03 Aug 2026 - selected evaluation tab now controls its stats and Lumin or Spider view
  // Updated - 03 Aug 2026 - enlarged Trend carousel cards and spider charts
  // Updated - 04 Aug 2026 - reporting enhancement (to be eventually published to MS PowerPoint and other doc output formats)
  // Updated - 06 Aug 2026 - reporting enhancement multi-eval powerpoint
  // Updated - 08 Aug 2026 - reporting enhancement single-eval pdf
  // Updated - 08 Aug 2026 - reporting enhancement multi-eval pdf
  // Updated - 08 Aug 2026 - reporting enhancement single and multi-eval inline email.
  // Updated - 16 Aug 2026 - reporting access based on useAuth
  // Updated - 21 Aug 2026 - team evaluation additions

  // 0 - 16 Aug 2026 - Evaluation report access controls
  const {
    isEtCoach,
    isEtAdmin,
    isSponsor,
  } =
    useAuth()


  const canManageEvaluationReportArtifacts =
    isEtCoach ||
    isEtAdmin


  const canEmailEvaluationPdf =
    canManageEvaluationReportArtifacts ||
    isSponsor
  

  // 1 - Session challenge section display state
  const [
    showChallengeDetails,
    setShowChallengeDetails,
  ] = useState(false)

  const [
    showChallengeSections,
    setShowChallengeSections,
  ] = useState({})


  // 2 - Challenge instructions and dimension display state
  const [
    showInstructionDetails,
    setShowInstructionDetails,
  ] = useState({})

  const [
    showChallengeHeadDetails,
    setShowChallengeHeadDetails,
  ] = useState({})

  const [
    showChallengeDimDetails,
    setShowChallengeDimDetails,
  ] = useState({})

  const [
    showChallengeDimEvalDetails,
    setShowChallengeDimEvalDetails,
  ] = useState({})


  // 3 - Evaluation history display state
  // The top level tab controls whether the challenge shows Trend
  // or one selected evaluation snapshot.
  const [
    activeEvaluationTabByChallengeId,
    setActiveEvaluationTabByChallengeId,
  ] = useState({})

  // Each evaluation snapshot retains its own Lumin or Spider selection.
  const [
    evaluationViewBySnapshotKey,
    setEvaluationViewBySnapshotKey,
  ] = useState({})

  // 3a - 04 Aug 2026 - Temporary evaluation report preview display state
  const [
    showReportPreviewByChallengeId,
    setShowReportPreviewByChallengeId,
  ] = useState({})


  // 4 - Self evaluation modal state
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState(false)

  const [
    triggeringUrl,
    setTriggeringUrl,
  ] = useState(null)

  const [
    activeChallengeId,
    setActiveChallengeId,
  ] = useState(null)

  const [
    existingEvalData,
    setExistingEvalData,
  ] = useState(null)

  const [
    mode,
    setMode,
  ] = useState("create")


  // 5 - Fetch self, coach and sponsor evaluations
  const {
    data: userChallengeSelfEvalData,
    refetch: refetchUserChallengeSelfEvals,
  } = useGetUserChallengeSelfEvalsQuery()

  const {
    data: userChallengeCoachEvalData,
  } = useGetUserChallengeCoachEvalsQuery()

  const {
    data: userChallengeSponsorEvalData,
  } = useGetUserChallengeSponsorEvalsQuery()

  // 21 Aug 2026 - Team evaluations
  const {
    data: userChallengeTeamEvalData,
  } = useGetUserChallengeTeamEvalsQuery()

  // 5a - 04 Aug 2026 - Generate and download evaluation PowerPoint
  const [
    downloadIndividualEvaluationPowerPoint,
    {
      isLoading:
        isDownloadingEvaluationPowerPoint,
    },
  ] =
    useDownloadIndividualChallengeEvaluationPowerPointMutation()

  // 5b - 06 Aug 2026
  // Generate and download all evaluation snapshots for one challenge.
  const [
    downloadMultiEvaluationPowerPoint,
    {
      isLoading:
        isDownloadingMultiEvaluationPowerPoint,
    },
  ] =
    useDownloadMultiChallengeEvaluationPowerPointMutation()

    // 5c - 08 Aug 2026
  // Generate and download one evaluation snapshot as PDF.
  const [
    downloadIndividualEvaluationPdf,
    {
      isLoading:
        isDownloadingEvaluationPdf,
    },
  ] =
    useDownloadIndividualChallengeEvaluationPdfMutation()


  // 5d - 08 Aug 2026
  // Generate and download the complete evaluation journey as PDF.
  const [
    downloadMultiEvaluationPdf,
    {
      isLoading:
        isDownloadingMultiEvaluationPdf,
    },
  ] =
    useDownloadMultiChallengeEvaluationPdfMutation()

  // 5e1 - 08 Aug 2026
  // Email one exact evaluation PDF to the evaluated user.
  const [
    // emailIndividualEvaluationReport,
    emailIndividualEvaluationPdf,
    {
      isLoading:
        // isEmailingEvaluationReport,
        isEmailingEvaluationPdf,
    },
  ] =
    useEmailIndividualChallengeEvaluationPdfMutation()
    // useEmailIndividualChallengeEvaluationReportMutation()

  // 5e2 - 08 Aug 2026
  // Email one exact evaluation as inline report content.
  const [
    emailIndividualEvaluationInline,
    {
      isLoading:
        isEmailingEvaluationInline,
    },
  ] =
    useEmailIndividualChallengeEvaluationInlineMutation()


  // 5f1 - 08 Aug 2026
  // Email the complete evaluation journey PDF to the evaluated user.
  const [
    // emailMultiEvaluationReport,
    emailMultiEvaluationPdf,
    {
      isLoading:
        // isEmailingMultiEvaluationReport,
        isEmailingMultiEvaluationPdf,
    },
  ] =
    // useEmailMultiChallengeEvaluationReportMutation()  
    useEmailMultiChallengeEvaluationPdfMutation() 
  
  // 5f2 - 08 Aug 2026
  // Email the complete evaluation journey as inline report content.
  const [
    emailMultiEvaluationInline,
    {
      isLoading:
        isEmailingMultiEvaluationInline,
    },
  ] =
    useEmailMultiChallengeEvaluationInlineMutation()


  // 6 - Normalize RTK entity adapter results into arrays
  const extractEntitiesFromAdapter = (
    adapterData
  ) => {
    if (
      !adapterData?.ids ||
      !adapterData?.entities
    ) {
      return []
    }

    return adapterData.ids
      .map((id) =>
        adapterData.entities[id]
      )
      .filter(Boolean)
  }


  const userChallengeSelfEvals =
    useMemo(() => {
      return extractEntitiesFromAdapter(
        userChallengeSelfEvalData
      )
    }, [userChallengeSelfEvalData])


  const userChallengeCoachEvals =
    useMemo(() => {
      return extractEntitiesFromAdapter(
        userChallengeCoachEvalData
      )
    }, [userChallengeCoachEvalData])


  const userChallengeSponsorEvals =
    useMemo(() => {
      return extractEntitiesFromAdapter(
        userChallengeSponsorEvalData
      )
    }, [userChallengeSponsorEvalData])

  // 21 Aug 2026
  const userChallengeTeamEvals =
    useMemo(() => {
      return extractEntitiesFromAdapter(
        userChallengeTeamEvalData
      )
    }, [userChallengeTeamEvalData])


  // 7a - Normalize populated and non populated Mongo references
  const getIdValue = (value) => {
    return (
      value?._id?.toString() ||
      value?.toString() ||
      ""
    )
  }

  // 7b - 04 Aug 2026 - Get Safe File Name Part for MS PPT generation
  const getSafeFileNamePart = (
    value,
    fallback
  ) => {
    const normalizedValue =
      String(value || fallback)
        .trim()
        .replace(
          /[^a-z0-9]+/gi,
          "_"
        )
        .replace(
          /^_+|_+$/g,
          ""
        )

    return (
      normalizedValue ||
      fallback
    )
  }


  // 8 - Normalize score values
  const normalizeEvalScore = (score) => {
    if (
      score === undefined ||
      score === null ||
      score === ""
    ) {
      return null
    }

    const numericScore = Number(score)

    return Number.isFinite(numericScore)
      ? numericScore
      : null
  }


  // 9 - Sort evaluation history newest first
  const sortEvaluationHistory = (
    evaluations = []
  ) => {
    return [...evaluations].sort(
      (evaluationA, evaluationB) => {
        const evaluationNumberDifference =
          Number(
            evaluationB?.evaluationNumber || 0
          ) -
          Number(
            evaluationA?.evaluationNumber || 0
          )

        if (evaluationNumberDifference !== 0) {
          return evaluationNumberDifference
        }

        const dateA = new Date(
          evaluationA?.evaluatedAt ||
          evaluationA?.createdAt ||
          0
        ).getTime()

        const dateB = new Date(
          evaluationB?.evaluatedAt ||
          evaluationB?.createdAt ||
          0
        ).getTime()

        return dateB - dateA
      }
    )
  }


  // 10 - Format evaluation date
  const formatEvaluationDate = (
    dateValue
  ) => {
    if (!dateValue) {
      return "Date unavailable"
    }

    const parsedDate = new Date(
      dateValue
    )

    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return "Date unavailable"
    }

    return new Intl.DateTimeFormat(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    ).format(parsedDate)
  }


  // 11 - Calculate one self evaluation average
  const getSelfEvaluationAverage = (
    evaluation
  ) => {
    const scores =
      evaluation?.userChallengeEvals
        ?.map((dimensionEvaluation) =>
          normalizeEvalScore(
            dimensionEvaluation
              ?.userChallengeDimSelfScore
          )
        )
        .filter(
          (score) => score !== null
        ) || []

    if (!scores.length) {
      return null
    }

    const total = scores.reduce(
      (sum, score) => sum + score,
      0
    )

    return Number(
      (total / scores.length).toFixed(1)
    )
  }


  // 12 - Shorten dimension labels for the spider chart
  const getShortDimensionName = (
    dimensionName,
    maximumLength = 22
  ) => {
    if (!dimensionName) {
      return "Dimension"
    }

    return dimensionName.length >
      maximumLength
      ? `${dimensionName.slice(
          0,
          maximumLength
        )}...`
      : dimensionName
  }


  // 13 - Evaluator display helpers
  const getFirstName = (user) => {
    const evaluatorName =
      user?.name ||
      user?.userId?.name ||
      ""

    return (
      evaluatorName.split(" ")[0] ||
      "User"
    )
  }


  const getUsername = (user) => {
    return (
      user?.username ||
      user?.userId?.username ||
      ""
    )
  }


  // 14 - Current session challenge IDs
  const sessionChallengeIds =
    useMemo(() => {
      return new Set(
        sessionChallenges
          .map((challenge) =>
            getIdValue(challenge?._id)
          )
          .filter(Boolean)
      )
    }, [sessionChallenges])


  // 15 - Match evaluations to the current user, organization, trail, session and challenge set
  // Evaluation history intentionally excludes cohortId.
  // The stored cohort remains historical context, while the
  // user can continue to see longitudinal history after a move.
  const matchesEvaluationHistoryContext = (
    evalItem
  ) => {
    const evaluationUserOrgId =
      evalItem?.userId?.userOrgId?._id ||
      evalItem?.userId?.userOrgId ||
      evalItem?.userOrgId

    return (
      getIdValue(evalItem?.userId) ===
        getIdValue(userId) &&
      getIdValue(evaluationUserOrgId) ===
        getIdValue(userOrgId) &&
      getIdValue(evalItem?.trailId) ===
        getIdValue(trailId) &&
      getIdValue(evalItem?.sessionId) ===
        getIdValue(sessionId) &&
      sessionChallengeIds.has(
        getIdValue(
          evalItem?.challengeId
        )
      )
    )
  }


  // 16 - Filter all evaluator histories to the current session context
  const filteredUserChallengeSelfEvals =
    useMemo(() => {
      return sortEvaluationHistory(
        userChallengeSelfEvals.filter(
          matchesEvaluationHistoryContext
        )
      )
    }, [
      userChallengeSelfEvals,
      userId,
      userOrgId,
      trailId,
      sessionId,
      sessionChallengeIds,
    ])


  const filteredUserChallengeCoachEvals =
    useMemo(() => {
      return sortEvaluationHistory(
        userChallengeCoachEvals.filter(
          matchesEvaluationHistoryContext
        )
      )
    }, [
      userChallengeCoachEvals,
      userId,
      userOrgId,
      trailId,
      sessionId,
      sessionChallengeIds,
    ])


  const filteredUserChallengeSponsorEvals =
    useMemo(() => {
      return sortEvaluationHistory(
        userChallengeSponsorEvals.filter(
          matchesEvaluationHistoryContext
        )
      )
    }, [
      userChallengeSponsorEvals,
      userId,
      userOrgId,
      trailId,
      sessionId,
      sessionChallengeIds,
    ])
  
  // 21 Aug 2026
  const filteredUserChallengeTeamEvals =
    useMemo(() => {
      return sortEvaluationHistory(
        userChallengeTeamEvals.filter(
          matchesEvaluationHistoryContext
        )
      )
    }, [
      userChallengeTeamEvals,
      userId,
      userOrgId,
      trailId,
      sessionId,
      sessionChallengeIds,
    ])


  // 17 - Group one evaluator type by challenge
  const buildEvaluationHistoryByChallenge =
    (evaluations = []) => {
      return sessionChallenges.reduce(
        (
          accumulator,
          challenge
        ) => {
          const challengeKey =
            getIdValue(challenge?._id)

          accumulator[challengeKey] =
            sortEvaluationHistory(
              evaluations.filter(
                (evaluation) =>
                  getIdValue(
                    evaluation?.challengeId
                  ) === challengeKey
              )
            )

          return accumulator
        },
        {}
      )
    }


  const selfEvaluationHistoryByChallengeId =
    useMemo(() => {
      return buildEvaluationHistoryByChallenge(
        filteredUserChallengeSelfEvals
      )
    }, [
      sessionChallenges,
      filteredUserChallengeSelfEvals,
    ])


  const coachEvaluationHistoryByChallengeId =
    useMemo(() => {
      return buildEvaluationHistoryByChallenge(
        filteredUserChallengeCoachEvals
      )
    }, [
      sessionChallenges,
      filteredUserChallengeCoachEvals,
    ])


  const sponsorEvaluationHistoryByChallengeId =
    useMemo(() => {
      return buildEvaluationHistoryByChallenge(
        filteredUserChallengeSponsorEvals
      )
    }, [
      sessionChallenges,
      filteredUserChallengeSponsorEvals,
    ])
  
  // 21 Aug 2026
  const teamEvaluationHistoryByChallengeId =
    useMemo(() => {
      return buildEvaluationHistoryByChallenge(
        filteredUserChallengeTeamEvals
      )
    }, [
      sessionChallenges,
      filteredUserChallengeTeamEvals,
    ])


  // 18 - Build exact evaluation number snapshots for every challenge
  // Important:
  // A coach or sponsor evaluation is included only when its evaluationNumber
  // exactly matches the self evaluationNumber for the snapshot.
  // This prevents Coach Eval 2 or Sponsor Eval 1 from appearing inside User Eval 3.
  const evaluationSnapshotsByChallengeId =
    useMemo(() => {
      return sessionChallenges.reduce(
        (
          accumulator,
          challenge
        ) => {
          const challengeId =
            getIdValue(challenge?._id)

          const selfHistory =
            selfEvaluationHistoryByChallengeId[
              challengeId
            ] || []

          const coachHistory =
            coachEvaluationHistoryByChallengeId[
              challengeId
            ] || []

          const sponsorHistory =
            sponsorEvaluationHistoryByChallengeId[
              challengeId
            ] || []
          
          // 21 Aug 2026
          const teamHistory =
            teamEvaluationHistoryByChallengeId[
              challengeId
            ] || []

          accumulator[challengeId] =
            selfHistory.map(
              (selfEvaluation) => {
                const evaluationNumber =
                  Number(
                    selfEvaluation?.evaluationNumber || 1
                  )

                const coachEvaluations =
                  coachHistory.filter(
                    (evaluation) =>
                      Number(
                        evaluation?.evaluationNumber || 1
                      ) === evaluationNumber
                  )

                const sponsorEvaluations =
                  sponsorHistory.filter(
                    (evaluation) =>
                      Number(
                        evaluation?.evaluationNumber || 1
                      ) === evaluationNumber
                  )

                //21 Aug 2026
                const teamEvaluations =
                  teamHistory.filter(
                    (evaluation) =>
                      Number(
                        evaluation?.evaluationNumber || 1
                      ) === evaluationNumber
                  )

                return {
                  snapshotKey:
                    `${challengeId}-evaluation-${evaluationNumber}-${getIdValue(
                      selfEvaluation?._id
                    )}`,
                  evaluationNumber,
                  selfEvaluation,
                  coachEvaluations,
                  sponsorEvaluations,
                  teamEvaluations,    // 21 Aug 2026
                }
              }
            )

          return accumulator
        },
        {}
      )
    }, [
      sessionChallenges,
      selfEvaluationHistoryByChallengeId,
      coachEvaluationHistoryByChallengeId,
      sponsorEvaluationHistoryByChallengeId,
      teamEvaluationHistoryByChallengeId,   // 21 Aug 2026
    ])

  // 19 - Build one radar configuration from one exact evaluation snapshot
  const buildRadarConfiguration = (
    challenge,
    snapshot
  ) => {
    const selfEvaluation =
      snapshot?.selfEvaluation || null

    const coachEvaluations =
      snapshot?.coachEvaluations || []

    const sponsorEvaluations =
      snapshot?.sponsorEvaluations || []

    // 21 Aug 2026
    const teamEvaluations =
      snapshot?.teamEvaluations || []

    const coachSeries =
      coachEvaluations.map(
        (evaluation, evaluatorIndex) => {
          const coachId =
            getIdValue(
              evaluation?.coachId
            )

          return {
            id:
              `coach-${coachId}-${getIdValue(
                evaluation?._id
              )}`,
            dataKey:
              `coach_${coachId}_${evaluatorIndex}`,
            label:
              `Coach ${getFirstName(
                evaluation?.coachId
              )}`,
            evaluation,
          }
        }
      )

    const sponsorSeries =
      sponsorEvaluations.map(
        (evaluation, evaluatorIndex) => {
          const sponsorId =
            getIdValue(
              evaluation?.sponsorId
            )

          return {
            id:
              `sponsor-${sponsorId}-${getIdValue(
                evaluation?._id
              )}`,
            dataKey:
              `sponsor_${sponsorId}_${evaluatorIndex}`,
            label:
              `Sponsor ${getFirstName(
                evaluation?.sponsorId
              )}`,
            evaluation,
          }
        }
      )
    
    // 21 Aug 2026
    const teamSeries =
      teamEvaluations.length > 0
        ? {
            id: "team",
            dataKey: "teamScore",
            label: "Team",
          }
        : null

    const evaluatorSeries = [
      ...(selfEvaluation
        ? [
            {
              id: "self",
              dataKey: "selfScore",
              label: "You",
              evaluation:
                selfEvaluation,
            },
          ]
        : []),
      ...coachSeries,
      ...sponsorSeries,
      // 21 Aug 2026
      ...(teamSeries
      ? [
          teamSeries,
        ]
      : []),
    ]

    const chartData =
      challenge
        ?.challengeDimensions
        ?.map(
          (
            dimension,
            dimensionIndex
          ) => {
            const dimensionId =
              getIdValue(
                dimension
                  ?.challengeDimId
              )

            const dimensionName =
              dimension
                ?.challengeDimId
                ?.challengeDimensionName ||
              `Dimension ${
                dimensionIndex + 1
              }`

            const chartRow = {
              dimensionId,
              dimensionName,
              shortDimensionName:
                getShortDimensionName(
                  dimensionName
                ),
              headName:
                dimension
                  ?.challengeDimId
                  ?.challengeHeadId
                  ?.challengeHeadName ||
                "Dimension",
              selfScore: null,
            }

            const selfDimensionEval =
              selfEvaluation
                ?.userChallengeEvals
                ?.find(
                  (evaluation) =>
                    getIdValue(
                      evaluation
                        ?.userChallengeDimId
                    ) === dimensionId
                )

            chartRow.selfScore =
              normalizeEvalScore(
                selfDimensionEval
                  ?.userChallengeDimSelfScore
              )

            coachSeries.forEach(
              (series) => {
                const coachDimensionEval =
                  series.evaluation
                    ?.userChallengeCoachEvals
                    ?.find(
                      (evaluation) =>
                        getIdValue(
                          evaluation
                            ?.userChallengeDimId
                        ) === dimensionId
                    )

                chartRow[
                  series.dataKey
                ] = normalizeEvalScore(
                  coachDimensionEval
                    ?.userChallengeDimCoachScore
                )
              }
            )

            sponsorSeries.forEach(
              (series) => {
                const sponsorDimensionEval =
                  series.evaluation
                    ?.userChallengeSponsorEvals
                    ?.find(
                      (evaluation) =>
                        getIdValue(
                          evaluation
                            ?.userChallengeDimId
                        ) === dimensionId
                    )

                chartRow[
                  series.dataKey
                ] = normalizeEvalScore(
                  sponsorDimensionEval
                    ?.userChallengeDimSponsorScore
                )
              }
            )

            // 21 Aug 2026
            if (teamSeries) {

              const teamAggregate =
                getTeamDimensionAggregate(
                  teamEvaluations,
                  dimensionId,
                )
              
              chartRow.teamScore =
                teamAggregate.score
            }

            return chartRow
          }
        ) || []

    const hasChartData =
      chartData.some((chartRow) =>
        evaluatorSeries.some(
          (series) =>
            chartRow[
              series.dataKey
            ] !== null &&
            chartRow[
              series.dataKey
            ] !== undefined
        )
      )

    return {
      chartData,
      evaluatorSeries,
      hasChartData,
    }
  }


  // 20 - Build radar configurations for all evaluation snapshots
  const radarConfigurationBySnapshotKey =
    useMemo(() => {
      return sessionChallenges.reduce(
        (
          accumulator,
          challenge
        ) => {
          const challengeId =
            getIdValue(challenge?._id)

          const snapshots =
            evaluationSnapshotsByChallengeId[
              challengeId
            ] || []

          snapshots.forEach(
            (snapshot) => {
              accumulator[
                snapshot.snapshotKey
              ] = buildRadarConfiguration(
                challenge,
                snapshot
              )
            }
          )

          return accumulator
        },
        {}
      )
    }, [
      sessionChallenges,
      evaluationSnapshotsByChallengeId,
    ])


  // 21 - Evaluation view helpers
  const getEvaluationSnapshotView = (
    snapshotKey
  ) => {
    return (
      evaluationViewBySnapshotKey[
        snapshotKey
      ] || "lumin"
    )
  }


  const handleEvaluationSnapshotViewChange = (
    snapshotKey,
    selectedView
  ) => {
    setEvaluationViewBySnapshotKey(
      (previous) => ({
        ...previous,
        [snapshotKey]: selectedView,
      })
    )
  }


  const getActiveEvaluationTab = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    return (
      activeEvaluationTabByChallengeId[
        challengeKey
      ] || "trend"
    )
  }


  const handleActiveEvaluationTabChange = (
    challengeId,
    selectedTab
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setActiveEvaluationTabByChallengeId(
      (previous) => ({
        ...previous,
        [challengeKey]:
          selectedTab,
      })
    )

    // Prevent comments opened in one evaluation
    // from carrying into another evaluation.
    setShowChallengeDimEvalDetails({})

    // 04 Aug 2026
    // Close the temporary report preview when changing
    // between Trend and evaluation snapshots.
    setShowReportPreviewByChallengeId(
      (previous) => ({
        ...previous,
        [challengeKey]: false,
      })
    )
  }

  // 04 Aug 2026 - Handle Toggle Report Preview
  const handleToggleReportPreview = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowReportPreviewByChallengeId(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }

  // 04 Aug 2026 - Handle Download Evaluation Powerpoint
  const handleDownloadEvaluationPowerPoint =
    async ({
      challenge,
      challengeId,
      evaluationNumber,
    }) => {
      try {
        const powerPointBlob =
          await downloadIndividualEvaluationPowerPoint(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),

              evaluationNumber,
            }
          ).unwrap()

        if (
          !powerPointBlob ||
          !(powerPointBlob instanceof Blob)
        ) {
          throw new Error(
            "The PowerPoint response was not a valid file."
          )
        }

        const downloadUrl =
          window.URL.createObjectURL(
            powerPointBlob
          )

        const downloadLink =
          document.createElement(
            "a"
          )

        const userName =
          selfUserData?.name ||
          selfUserData?.username ||
          "User"

        const challengeName =
          challenge
            ?.challengeName ||
          "Challenge"

        downloadLink.href =
          downloadUrl

        downloadLink.download =
          `${getSafeFileNamePart(
            userName,
            "User"
          )}_${getSafeFileNamePart(
            challengeName,
            "Challenge"
          )}_Eval_${evaluationNumber}.pptx`

        document.body.appendChild(
          downloadLink
        )

        downloadLink.click()

        document.body.removeChild(
          downloadLink
        )

        window.URL.revokeObjectURL(
          downloadUrl
        )
      } catch (error) {
        console.error(
          "Evaluation PowerPoint download failed:",
          error
        )

        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "The PowerPoint could not be generated."

        window.alert(
          errorMessage
        )
      }
  }

  // 08 Aug 2026
  // Download one exact evaluation snapshot as PDF.
  const handleDownloadEvaluationPdf =
    async ({
      challenge,
      challengeId,
      evaluationNumber,
    }) => {
      try {
        const pdfBlob =
          await downloadIndividualEvaluationPdf(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),

              evaluationNumber,
            }
          ).unwrap()


        if (
          !pdfBlob ||
          !(
            pdfBlob instanceof
            Blob
          )
        ) {
          throw new Error(
            "The PDF response was not a valid file."
          )
        }


        const downloadUrl =
          window.URL
            .createObjectURL(
              pdfBlob
            )


        const downloadLink =
          document
            .createElement(
              "a"
            )


        const userName =
          selfUserData?.name ||
          selfUserData?.username ||
          "User"


        const challengeName =
          challenge
            ?.challengeName ||
          "Challenge"


        downloadLink.href =
          downloadUrl


        downloadLink.download =
          `${getSafeFileNamePart(
            userName,
            "User"
          )}_${getSafeFileNamePart(
            challengeName,
            "Challenge"
          )}_Eval_${evaluationNumber}.pdf`


        document.body
          .appendChild(
            downloadLink
          )


        downloadLink.click()


        document.body
          .removeChild(
            downloadLink
          )


        window.URL
          .revokeObjectURL(
            downloadUrl
          )
      } catch (
        error
      ) {
        console.error(
          "Evaluation PDF download failed:",
          error
        )


        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "The PDF could not be generated."


        window.alert(
          errorMessage
        )
      }
  }

  // 07 Aug 2026
  // Download the complete evaluation journey for one challenge.
  const handleDownloadMultiEvaluationPowerPoint =
    async ({
      challenge,
      challengeId,
    }) => {
      try {
        const powerPointBlob =
          await downloadMultiEvaluationPowerPoint(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),
            }
          ).unwrap()

        if (
          !powerPointBlob ||
          !(
            powerPointBlob instanceof
            Blob
          )
        ) {
          throw new Error(
            "The evaluation journey PowerPoint response was not a valid file."
          )
        }

        const downloadUrl =
          window.URL
            .createObjectURL(
              powerPointBlob
            )

        const downloadLink =
          document
            .createElement(
              "a"
            )

        const userName =
          selfUserData?.name ||
          selfUserData?.username ||
          "User"

        const challengeName =
          challenge
            ?.challengeName ||
          "Challenge"

        downloadLink.href =
          downloadUrl

        downloadLink.download =
          `${getSafeFileNamePart(
            userName,
            "User"
          )}_${getSafeFileNamePart(
            challengeName,
            "Challenge"
          )}_Evaluation_Journey.pptx`

        document.body
          .appendChild(
            downloadLink
          )

        downloadLink.click()

        document.body
          .removeChild(
            downloadLink
          )

        window.URL
          .revokeObjectURL(
            downloadUrl
          )
      } catch (error) {
        console.error(
          "Multi evaluation PowerPoint download failed:",
          error
        )

        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "The evaluation journey PowerPoint could not be generated."

        window.alert(
          errorMessage
        )
      }
  }

  // 08 Aug 2026
  // Download the complete evaluation journey as PDF.
  const handleDownloadMultiEvaluationPdf =
    async ({
      challenge,
      challengeId,
    }) => {
      try {
        const pdfBlob =
          await downloadMultiEvaluationPdf(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),
            }
          ).unwrap()


        if (
          !pdfBlob ||
          !(
            pdfBlob instanceof
            Blob
          )
        ) {
          throw new Error(
            "The evaluation journey PDF response was not a valid file."
          )
        }


        const downloadUrl =
          window.URL
            .createObjectURL(
              pdfBlob
            )


        const downloadLink =
          document
            .createElement(
              "a"
            )


        const userName =
          selfUserData?.name ||
          selfUserData?.username ||
          "User"


        const challengeName =
          challenge
            ?.challengeName ||
          "Challenge"


        downloadLink.href =
          downloadUrl


        downloadLink.download =
          `${getSafeFileNamePart(
            userName,
            "User"
          )}_${getSafeFileNamePart(
            challengeName,
            "Challenge"
          )}_Evaluation_Journey.pdf`


        document.body
          .appendChild(
            downloadLink
          )


        downloadLink.click()


        document.body
          .removeChild(
            downloadLink
          )


        window.URL
          .revokeObjectURL(
            downloadUrl
          )
      } catch (
        error
      ) {
        console.error(
          "Multi evaluation PDF download failed:",
          error
        )


        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "The evaluation journey PDF could not be generated."


        window.alert(
          errorMessage
        )
      }
  }

  // 08 Aug 2026
  // Email one exact evaluation PDF to the evaluated user.
  // const handleEmailEvaluationReport =
  const handleEmailEvaluationPdf =
    async ({
      challengeId,
      evaluationNumber,
    }) => {
      try {
        const result =
          // await emailIndividualEvaluationReport(
          await emailIndividualEvaluationPdf(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),

              evaluationNumber,
            }
          ).unwrap()


        window.alert(
          result?.message ||
          "Evaluation report emailed successfully."
        )
      } catch (
        error
      ) {
        console.error(
          "Evaluation report email failed:",
          error
        )


        window.alert(
          error?.data?.message ||
          error?.message ||
          "The evaluation report could not be emailed."
        )
      }
  }

  // 08 Aug 2026
  // Email one exact evaluation as inline HTML report content.
  const handleEmailEvaluationInline =
    async ({
      challengeId,
      evaluationNumber,
    }) => {
      try {
        const result =
          await emailIndividualEvaluationInline(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),

              evaluationNumber,
            }
          ).unwrap()


        window.alert(
          result?.message ||
          "Evaluation report emailed successfully."
        )
      } catch (
        error
      ) {
        console.error(
          "Inline evaluation report email failed:",
          error
        )


        window.alert(
          error?.data?.message ||
          error?.message ||
          "The evaluation report could not be emailed."
        )
      }
  }

  // 08 Aug 2026
  // Email the complete evaluation journey PDF to the evaluated user.
  // const handleEmailMultiEvaluationReport =
  const handleEmailMultiEvaluationPdf =
    async ({
      challengeId,
    }) => {
      try {
        const result =
          // await emailMultiEvaluationReport(
          await emailMultiEvaluationPdf(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),
            }
          ).unwrap()


        window.alert(
          result?.message ||
          "Evaluation journey emailed successfully."
        )
      } catch (
        error
      ) {
        console.error(
          "Evaluation journey email failed:",
          error
        )


        window.alert(
          error?.data?.message ||
          error?.message ||
          "The evaluation journey could not be emailed."
        )
      }
  }

  // 08 Aug 2026
  // Email the complete evaluation journey as inline HTML report content.
  const handleEmailMultiEvaluationInline =
    async ({
      challengeId,
    }) => {
      try {
        const result =
          await emailMultiEvaluationInline(
            {
              orgId:
                getIdValue(
                  userOrgId
                ),

              trailId:
                getIdValue(
                  trailId
                ),

              sessionId:
                getIdValue(
                  sessionId
                ),

              challengeId:
                getIdValue(
                  challengeId
                ),

              userId:
                getIdValue(
                  userId
                ),
            }
          ).unwrap()


        window.alert(
          result?.message ||
          "Evaluation journey emailed successfully."
        )
      } catch (
        error
      ) {
        console.error(
          "Inline evaluation journey email failed:",
          error
        )


        window.alert(
          error?.data?.message ||
          error?.message ||
          "The evaluation journey could not be emailed."
        )
      }
  }

  // 22 - General challenge display handlers
  const handleToggleChallengeSections = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowChallengeSections(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }


  const handleToggleInstructionDetails = (
    challengeId
  ) => {
    const challengeKey =
      getIdValue(challengeId)

    setShowInstructionDetails(
      (previous) => ({
        ...previous,
        [challengeKey]:
          !previous[challengeKey],
      })
    )
  }


  const handleToggleChallengeHeadDetails = (
    snapshotKey
  ) => {
    setShowChallengeHeadDetails(
      (previous) => ({
        ...previous,
        [snapshotKey]:
          !previous[snapshotKey],
      })
    )
  }


  const handleToggleChallengeDimDetails = (
    challengeHeadKey
  ) => {
    setShowChallengeDimDetails(
      (previous) => ({
        ...previous,
        [challengeHeadKey]:
          !previous[
            challengeHeadKey
          ],
      })
    )
  }


  const handleToggleChallengeDimEvalDetails =
    (evaluatorCommentKey) => {
      setShowChallengeDimEvalDetails(
        (previous) => ({
          ...previous,
          [evaluatorCommentKey]:
            !previous[
              evaluatorCommentKey
            ],
        })
      )
    }


  // 23 - Self evaluation modal handlers
  const handleEditEvaluation = (
    challengeId,
    evaluation
  ) => {
    setTriggeringUrl(
      window.location.pathname
    )

    setActiveChallengeId(
      getIdValue(challengeId)
    )

    setMode("update")
    setExistingEvalData(evaluation)
    setIsModalOpen(true)
  }


  const handleEvaluateAgain = (
    challengeId
  ) => {
    setTriggeringUrl(
      window.location.pathname
    )

    setActiveChallengeId(
      getIdValue(challengeId)
    )

    setMode("create")
    setExistingEvalData(null)
    setIsModalOpen(true)
  }


  const handleCloseChallengeEvalModal =
    () => {
      setIsModalOpen(false)
      setTriggeringUrl(null)
      setActiveChallengeId(null)
      setExistingEvalData(null)
      setMode("create")

      refetchUserChallengeSelfEvals()
    }


  // 24 - Lumin score color and tile helpers
  const getColorForScore = (score) => {
    const numericScore = Number(score)

    if (
      numericScore >= 0 &&
      numericScore < 0.9
    ) {
      return "bg-etpx-0"
    }

    if (
      numericScore >= 0.9 &&
      numericScore < 1.9
    ) {
      return "bg-etpx-1"
    }

    if (
      numericScore >= 1.9 &&
      numericScore < 2.9
    ) {
      return "bg-etpx-2"
    }

    if (
      numericScore >= 2.9 &&
      numericScore < 3.9
    ) {
      return "bg-etpx-3"
    }

    if (
      numericScore >= 3.9 &&
      numericScore < 4.9
    ) {
      return "bg-etpx-4"
    }

    if (numericScore >= 4.9) {
      return "bg-etpx-5"
    }

    return "bg-black"
  }


  const renderScoreTile = (score) => {
    const normalizedScore =
      normalizeEvalScore(score)

    if (normalizedScore === null) {
      return (
        <div className="flex h-14 w-14 items-center justify-center rounded border border-gray-300 bg-gradient-to-r from-gray-800 via-black to-gray-800 sm:h-16 sm:w-16">
          <p className="text-[8px] text-gray-400 sm:text-[9px]">
            No Eval
          </p>
        </div>
      )
    }

    return (
      <div
        className={`flex h-14 w-14 items-center justify-center rounded border border-gray-300 sm:h-16 sm:w-16 ${getColorForScore(
          normalizedScore
        )}`}
      >
        <p className="text-sm font-semibold text-white">
          {normalizedScore}
        </p>
      </div>
    )
  }


  // 25 - Check whether one evaluator has data for one challenge head
  const hasEvaluatorDataForHead = (
    matchingEval,
    dimensions,
    evaluationField
  ) => {
    if (!matchingEval) {
      return false
    }

    const dimensionIds = new Set(
      dimensions.map((dimension) =>
        getIdValue(
          dimension?.challengeDimId
        )
      )
    )

    return Boolean(
      matchingEval[
        evaluationField
      ]?.some((evaluation) =>
        dimensionIds.has(
          getIdValue(
            evaluation
              ?.userChallengeDimId
          )
        )
      )
    )
  }

  // 21 Aug 2026 - Check whether Team has evaluation data
  // for at least one dimension within a challenge head.
  const hasTeamEvaluatorDataForHead = (
    teamEvaluations = [],
    dimensions = []
  ) => {

    const dimensionIds =
      new Set(
        dimensions.map(
          (dimension) =>
            getIdValue(
              dimension
                ?.challengeDimId
            )
        )
      )


    return teamEvaluations.some(
      (teamEvaluation) =>
        teamEvaluation
          ?.userChallengeTeamEvals
          ?.some(
            (evaluation) =>
              dimensionIds.has(
                getIdValue(
                  evaluation
                    ?.userChallengeDimId
                )
              )
          )
    )
  }

  const challengeCount =
    sessionChallenges?.length || 0


  if (challengeCount === 0) {
    return null
  }


  return (
    <div className="m-1 rounded border border-white p-1">

      {/* 26 - Session Challenges Header */}
      <div className="m-1 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 p-1">

        <button
          type="button"
          onClick={() =>
            setShowChallengeDetails(
              (previous) => !previous
            )
          }
          className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-white"
          aria-label={
            showChallengeDetails
              ? "Collapse session challenges"
              : "Expand session challenges"
          }
        >
          {showChallengeDetails ? (
            <MinusIcon className="h-5 w-5 text-orange-600" />
          ) : (
            <PlusIcon className="h-5 w-5 text-orange-600" />
          )}
        </button>


        <div className="flex min-w-0 items-center">

          <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200">
            <EtChallengeIcon1SVGComp className="h-5 w-5 text-orange-600" />
          </div>

          <div className="min-w-0">

            <p className="truncate font-bold">
              Session Challenges
            </p>

            <p className="truncate text-[10px] italic text-gray-600">
              Test your client understanding muscle.
            </p>

          </div>

        </div>


        <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-gray-50 px-1">
          <p className="text-[10px] text-orange-600">
            {challengeCount}
          </p>
        </div>

      </div>


      {/* 27 - Challenge List */}
      {showChallengeDetails && (
        <div>

          {sessionChallenges.map(
            (
              challenge,
              challengeIndex
            ) => {

              const challengeId =
                getIdValue(challenge?._id)

              const evaluationSnapshots =
                evaluationSnapshotsByChallengeId[
                  challengeId
                ] || []

              const latestSnapshot =
                evaluationSnapshots[0] || null

              const activeEvaluationTab =
                getActiveEvaluationTab(
                  challengeId
                )

              const selectedSnapshot =
                activeEvaluationTab === "trend"
                  ? null
                  : evaluationSnapshots.find(
                      (snapshot) =>
                        snapshot.snapshotKey ===
                        activeEvaluationTab
                    ) ||
                    latestSnapshot ||
                    null

              const selectedSnapshotView =
                selectedSnapshot
                  ? getEvaluationSnapshotView(
                      selectedSnapshot.snapshotKey
                    )
                  : "lumin"

              const selectedRadarConfiguration =
                selectedSnapshot
                  ? radarConfigurationBySnapshotKey[
                      selectedSnapshot.snapshotKey
                    ]
                  : null

              const selectedCompletedDimensions =
                selectedSnapshot
                  ?.selfEvaluation
                  ?.userChallengeEvals
                  ?.filter(
                    (dimensionEvaluation) =>
                      normalizeEvalScore(
                        dimensionEvaluation
                          ?.userChallengeDimSelfScore
                      ) !== null
                  )
                  .length || 0

              const groupedDimensions =
                challenge
                  ?.challengeDimensions
                  ?.reduce(
                    (
                      accumulator,
                      dimension
                    ) => {
                      const headName =
                        dimension
                          ?.challengeDimId
                          ?.challengeHeadId
                          ?.challengeHeadName ||
                        "Unknown Head"

                      if (
                        !accumulator[
                          headName
                        ]
                      ) {
                        accumulator[
                          headName
                        ] = []
                      }

                      accumulator[
                        headName
                      ].push(dimension)

                      return accumulator
                    },
                    {}
                  ) || {}


              return (
                <div
                  key={
                    challengeId ||
                    challengeIndex
                  }
                  className="m-1 rounded bg-gray-100 p-1"
                >

                  {/* 28 - Challenge Header */}
                  <div className="m-1 flex items-start p-1">

                    <button
                      type="button"
                      onClick={() =>
                        handleToggleChallengeSections(
                          challengeId
                        )
                      }
                      className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                    >
                      {showChallengeSections[
                        challengeId
                      ] ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                      )}
                    </button>


                    <div className="min-w-0 flex-1">

                      <p className="font-bold">
                        {challenge.challengeName}
                      </p>

                      <p className="text-[10px] italic text-gray-600">
                        {challenge.challengeDesc}
                      </p>

                    </div>

                  </div>


                  {showChallengeSections[
                    challengeId
                  ] && (
                    <div className="space-y-3 px-1 pb-2">

                      {/* 29 - Challenge Instructions */}
                      <div className="mx-1 rounded bg-gray-200">

                        <div className="m-1 flex items-center p-1">

                          <button
                            type="button"
                            onClick={() =>
                              handleToggleInstructionDetails(
                                challengeId
                              )
                            }
                            className="mr-2 flex h-6 w-6 items-center justify-center rounded-full hover:bg-white"
                          >
                            {showInstructionDetails[
                              challengeId
                            ] ? (
                              <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                              <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                          </button>

                          <div className="mr-2 flex h-7 w-7 items-center justify-center rounded-full hover:bg-white">
                            <WrenchIcon className="h-6 w-6 text-orange-600" />
                          </div>

                          <p className="font-bold text-gray-500">
                            Instructions
                          </p>

                        </div>


                        {showInstructionDetails[
                          challengeId
                        ] &&
                          challenge
                            ?.challengeInstructions
                            ?.length > 0 && (
                            <div className="m-1 rounded p-1">

                              <div className="m-1 rounded bg-gray-300 p-1">

                                {challenge.challengeInstructions.map(
                                  (
                                    instruction,
                                    instructionIndex
                                  ) => (
                                    <div
                                      key={
                                        instruction?._id ||
                                        instructionIndex
                                      }
                                      className="m-1 rounded bg-gray-100 p-2"
                                    >

                                      <div className="flex items-start">

                                        <p className="mr-2 shrink-0 font-bold text-gray-400">
                                          {instructionIndex + 1}.
                                        </p>

                                        <p className="text-[10px]">
                                          {instruction.instruction}
                                        </p>

                                      </div>

                                    </div>
                                  )
                                )}

                              </div>

                            </div>
                          )}

                      </div>


                      {/* 30 - Evaluation History Navigation */}
                      <div className="mx-1 rounded border border-gray-200 bg-white">

                        <div className="flex flex-col gap-2 border-b border-gray-200 p-2 sm:flex-row sm:items-center sm:justify-between">

                          <div className="min-w-0 overflow-x-auto">

                            <div className="flex min-w-max items-center">

                              <button
                                type="button"
                                onClick={() =>
                                  handleActiveEvaluationTabChange(
                                    challengeId,
                                    "trend"
                                  )
                                }
                                className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                                  activeEvaluationTab === "trend"
                                    ? "border-orange-600 text-orange-600"
                                    : "border-transparent text-gray-500 hover:text-orange-600"
                                }`}
                              >
                                Trend
                              </button>


                              {evaluationSnapshots.map(
                                (snapshot) => (
                                  <button
                                    key={
                                      `tab-${snapshot.snapshotKey}`
                                    }
                                    type="button"
                                    onClick={() =>
                                      handleActiveEvaluationTabChange(
                                        challengeId,
                                        snapshot.snapshotKey
                                      )
                                    }
                                    className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                                      activeEvaluationTab ===
                                      snapshot.snapshotKey
                                        ? "border-orange-600 text-orange-600"
                                        : "border-transparent text-gray-500 hover:text-orange-600"
                                    }`}
                                  >
                                    Eval {snapshot.evaluationNumber}
                                  </button>
                                )
                              )}

                            </div>

                          </div>


                          <button
                            type="button"
                            onClick={() =>
                              handleEvaluateAgain(
                                challengeId
                              )
                            }
                            className="shrink-0 self-end rounded-full bg-orange-600 px-4 py-1.5 text-[10px] font-semibold text-white hover:bg-orange-700 sm:self-auto"
                          >
                            {evaluationSnapshots.length
                              ? "Evaluate Again"
                              : "Evaluate"}
                          </button>

                        </div>


                        <div className="p-2">

                          {evaluationSnapshots.length === 0 ? (
                            <div className="rounded border border-orange-200 bg-orange-50 p-4">

                              <p className="text-xs font-semibold text-orange-600">
                                No evaluation history is available for this challenge yet.
                              </p>

                              <p className="mt-1 text-[10px] text-orange-500">
                                Complete the first evaluation to establish a baseline.
                              </p>

                            </div>
                          ) : activeEvaluationTab === "trend" ? (
                            <div>

                              {/* 06 Aug 2026 - Evaluation journey report download */}
                              <div className="mb-3 flex flex-col gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">

                                <div>

                                  <p className="text-xs font-bold text-gray-700">
                                    Evaluation Trend
                                  </p>

                                  <p className="text-[9px] text-gray-500">
                                    Each card plots one exact evaluation number snapshot.
                                  </p>

                                </div>

                                {/* 08 Aug 2026 */}
                                <div className="flex flex-wrap items-center gap-2">

                                {/* 16 Aug 2026 */}
                                {canManageEvaluationReportArtifacts && (
                                  <button
                                    type="button"
                                    disabled={
                                      isDownloadingMultiEvaluationPowerPoint ||
                                      evaluationSnapshots.length === 0
                                    }
                                    onClick={() =>
                                      handleDownloadMultiEvaluationPowerPoint({
                                        challenge,
                                        challengeId,
                                      })
                                    }
                                    className={`shrink-0 rounded-full border px-4 py-1.5 text-[10px] font-semibold transition ${
                                      isDownloadingMultiEvaluationPowerPoint ||
                                      evaluationSnapshots.length === 0
                                        ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                        : "border-lime-600 bg-lime-600 text-white hover:bg-lime-700"
                                    }`}
                                  >
                                    {isDownloadingMultiEvaluationPowerPoint
                                      ? "Generating Journey PowerPoint..."
                                      : "Download PowerPoint"}
                                  </button>
                                )}

                                {/* 16 Aug 2026 */}
                                {canManageEvaluationReportArtifacts && (  
                                  <button
                                    type="button"
                                    disabled={
                                      isDownloadingMultiEvaluationPdf ||
                                      evaluationSnapshots.length === 0
                                    }
                                    onClick={() =>
                                      handleDownloadMultiEvaluationPdf({
                                        challenge,
                                        challengeId,
                                      })
                                    }
                                    className={`shrink-0 rounded-full border px-4 py-1.5 text-[10px] font-semibold transition ${
                                      isDownloadingMultiEvaluationPdf ||
                                      evaluationSnapshots.length === 0
                                        ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                        : "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                                    }`}
                                  >
                                    {isDownloadingMultiEvaluationPdf
                                      ? "Generating Journey PDF..."
                                      : "Download PDF"}
                                  </button>
                                )}

                                {/* 16 Aug 2026 */}
                                {canEmailEvaluationPdf && (
                                  <button
                                    type="button"
                                    disabled={
                                      // isEmailingMultiEvaluationReport ||
                                      isEmailingMultiEvaluationPdf ||
                                      evaluationSnapshots.length === 0
                                    }
                                    onClick={() =>
                                      // handleEmailMultiEvaluationReport({
                                      handleEmailMultiEvaluationPdf({
                                        challengeId,
                                      })
                                    }
                                    className={`shrink-0 rounded-full border px-4 py-1.5 text-[10px] font-semibold transition ${
                                      // isEmailingMultiEvaluationReport ||
                                      isEmailingMultiEvaluationPdf ||
                                      evaluationSnapshots.length === 0
                                        ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                        : "border-purple-600 bg-purple-600 text-white hover:bg-purple-700"
                                    }`}
                                  >
                                    {/* {isEmailingMultiEvaluationReport */}
                                    {isEmailingMultiEvaluationPdf
                                      ? "Emailing PDF..."
                                      : "Email PDF"}
                                  </button>
                                )}

                                  <button
                                    type="button"
                                    disabled={
                                      isEmailingMultiEvaluationInline ||
                                      evaluationSnapshots.length === 0
                                    }
                                    onClick={() =>
                                      handleEmailMultiEvaluationInline({
                                        challengeId,
                                      })
                                    }
                                    className={`shrink-0 rounded-full border px-4 py-1.5 text-[10px] font-semibold transition ${
                                      isEmailingMultiEvaluationInline ||
                                      evaluationSnapshots.length === 0
                                        ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                        : "border-orange-600 bg-orange-600 text-white hover:bg-orange-700"
                                    }`}
                                  >
                                    {isEmailingMultiEvaluationInline
                                      ? "Emailing Report..."
                                      : "Email Report"}
                                  </button>

                                </div>

                                {/* <button
                                  type="button"
                                  disabled={
                                    isDownloadingMultiEvaluationPowerPoint ||
                                    evaluationSnapshots.length === 0
                                  }
                                  onClick={() =>
                                    handleDownloadMultiEvaluationPowerPoint({
                                      challenge,
                                      challengeId,
                                    })
                                  }
                                  className={`shrink-0 self-start rounded-full border px-4 py-1.5 text-[10px] font-semibold transition sm:self-auto ${
                                    isDownloadingMultiEvaluationPowerPoint ||
                                    evaluationSnapshots.length === 0
                                      ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                      : "border-lime-600 bg-lime-600 text-white hover:bg-lime-700"
                                  }`}
                                >
                                  {isDownloadingMultiEvaluationPowerPoint
                                    ? "Generating Journey PowerPoint..."
                                    : "Download Evaluation Journey"}
                                </button> */}

                              </div>

                              {/* <div className="mb-2 px-1">

                                <p className="text-xs font-bold text-gray-700">
                                  Evaluation Trend
                                </p>

                                <p className="text-[9px] text-gray-500">
                                  Each card plots one exact evaluation number snapshot.
                                </p>

                              </div> */}

                              <EvaluationTrendCarousel
                                snapshots={
                                  [...evaluationSnapshots].reverse()
                                }
                                radarConfigurationBySnapshotKey={
                                  radarConfigurationBySnapshotKey
                                }
                                formatEvaluationDate={
                                  formatEvaluationDate
                                }
                              />

                            </div>
                          ) : selectedSnapshot ? (
                            <div>

                              {/* 31 - Selected Evaluation Stats */}
                              <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3">

                                <div className="flex flex-wrap items-start justify-between gap-3">

                                  <div>

                                    <div className="flex flex-wrap items-center gap-2">

                                      <p className="text-xs font-bold text-gray-700">
                                        Evaluation {selectedSnapshot.evaluationNumber}
                                      </p>

                                      <span className="rounded-full bg-orange-50 px-2 py-1 text-[9px] font-semibold text-orange-600">
                                        Selected Evaluation
                                      </span>

                                      <span className="rounded-full bg-white px-2 py-1 text-[9px] capitalize text-gray-500">
                                        {selectedSnapshot
                                          .selfEvaluation
                                          ?.evaluationType
                                          ?.replace(
                                            "_",
                                            " "
                                          ) || "Evaluation"}
                                      </span>

                                    </div>

                                    <p className="mt-1 text-[10px] text-gray-500">
                                      Evaluated on{" "}
                                      {formatEvaluationDate(
                                        selectedSnapshot
                                          .selfEvaluation
                                          ?.evaluatedAt ||
                                        selectedSnapshot
                                          .selfEvaluation
                                          ?.createdAt
                                      )}
                                    </p>

                                    <p className="mt-1 text-[9px] text-gray-400">
                                      {selectedCompletedDimensions} of{" "}
                                      {challenge
                                        ?.challengeDimensions
                                        ?.length || 0}{" "}
                                      dimensions evaluated
                                    </p>

                                    <p className="mt-1 text-[9px] text-gray-400">
                                      Showing You,{" "}
                                      {selectedSnapshot.coachEvaluations.length}{" "}
                                      Coach and{" "}
                                      {selectedSnapshot.sponsorEvaluations.length}{" "}
                                      Sponsor evaluation
                                      {selectedSnapshot.coachEvaluations.length +
                                        selectedSnapshot.sponsorEvaluations.length ===
                                      1
                                        ? ""
                                        : "s"}{" "}
                                      for Eval {selectedSnapshot.evaluationNumber}.
                                    </p>

                                    {/* 04 Aug 2026 */}
                                    <div className="mt-3 flex flex-wrap items-center gap-2">

                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleEditEvaluation(
                                            challengeId,
                                            selectedSnapshot.selfEvaluation
                                          )
                                        }
                                        className="rounded-full border border-orange-300 bg-white px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"
                                      >
                                        Edit Eval {selectedSnapshot.evaluationNumber}
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleToggleReportPreview(
                                            challengeId
                                          )
                                        }
                                        className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                                          showReportPreviewByChallengeId[
                                            challengeId
                                          ]
                                            ? "border-orange-600 bg-orange-600 text-white"
                                            : "border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100"
                                        }`}
                                      >
                                        {showReportPreviewByChallengeId[
                                          challengeId
                                        ]
                                          ? "Hide Report Preview"
                                          : "Preview Report"}
                                      </button>

                                      {/* 16 Aug 2026 */}
                                      {canManageEvaluationReportArtifacts && (

                                        <button
                                          type="button"
                                          disabled={
                                            isDownloadingEvaluationPowerPoint
                                          }
                                          onClick={() =>
                                            handleDownloadEvaluationPowerPoint({
                                              challenge,
                                              challengeId,
                                              evaluationNumber:
                                                selectedSnapshot
                                                  .evaluationNumber,
                                            })
                                          }
                                          className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                                            isDownloadingEvaluationPowerPoint
                                              ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                              : "border-lime-600 bg-lime-600 text-white hover:bg-lime-700"
                                          }`}
                                        >
                                          {isDownloadingEvaluationPowerPoint
                                            ? "Generating PowerPoint..."
                                            : "Download PowerPoint"}
                                        </button>
                                      )}

                                      {/* 08 Aug 2026 */}
                                      {/* 16 Aug 2026 */}
                                      {canManageEvaluationReportArtifacts && (
                                        <button
                                          type="button"
                                          disabled={
                                            isDownloadingEvaluationPdf
                                          }
                                          onClick={() =>
                                            handleDownloadEvaluationPdf({
                                              challenge,
                                              challengeId,
                                              evaluationNumber:
                                                selectedSnapshot
                                                  .evaluationNumber,
                                            })
                                          }
                                          className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                                            isDownloadingEvaluationPdf
                                              ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                              : "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
                                          }`}
                                        >
                                          {isDownloadingEvaluationPdf
                                            ? "Generating PDF..."
                                            : "Download PDF"}
                                        </button>
                                      )}

                                      {/* 16 Aug 2026 */}
                                      {canEmailEvaluationPdf && (
                                        <button
                                          type="button"
                                          disabled={
                                            // isEmailingEvaluationReport
                                            isEmailingEvaluationPdf
                                          }
                                          onClick={() =>
                                            // handleEmailEvaluationReport({
                                            handleEmailEvaluationPdf({
                                              challengeId,
                                              evaluationNumber:
                                                selectedSnapshot
                                                  .evaluationNumber,
                                            })
                                          }
                                          className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                                            // isEmailingEvaluationReport
                                            isEmailingEvaluationPdf
                                              ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                              : "border-purple-600 bg-purple-600 text-white hover:bg-purple-700"
                                          }`}
                                        >
                                          {/* {isEmailingEvaluationReport */}
                                          {isEmailingEvaluationPdf
                                            ? "Emailing PDF..."
                                            : "Email PDF"}
                                        </button>
                                      )}

                                      <button
                                        type="button"
                                        disabled={
                                          isEmailingEvaluationInline
                                        }
                                        onClick={() =>
                                          handleEmailEvaluationInline({
                                            challengeId,
                                            evaluationNumber:
                                              selectedSnapshot
                                                .evaluationNumber,
                                          })
                                        }
                                        className={`rounded-full border px-3 py-1 text-[10px] font-semibold transition ${
                                          isEmailingEvaluationInline
                                            ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400"
                                            : "border-orange-600 bg-orange-600 text-white hover:bg-orange-700"
                                        }`}
                                      >
                                        {isEmailingEvaluationInline
                                          ? "Emailing Report..."
                                          : "Email Report"}
                                      </button>

                                    </div>
                                    {/* <button
                                      type="button"
                                      onClick={() =>
                                        handleEditEvaluation(
                                          challengeId,
                                          selectedSnapshot.selfEvaluation
                                        )
                                      }
                                      className="mt-3 rounded-full border border-orange-300 bg-white px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"
                                    >
                                      Edit Eval {selectedSnapshot.evaluationNumber}
                                    </button> */}

                                  </div>


                                  <div className="rounded bg-white px-4 py-3 text-center">

                                    <p className="text-[8px] uppercase tracking-wide text-gray-400">
                                      Self Average
                                    </p>

                                    <p className="text-xl font-bold text-orange-600">
                                      {getSelfEvaluationAverage(
                                        selectedSnapshot.selfEvaluation
                                      ) ?? "N/A"}
                                    </p>

                                  </div>

                                </div>

                              </div>

                              {/* 32a - 04 Aug 2026 - Temporary Normalized Report Preview */}
                              {showReportPreviewByChallengeId[
                                challengeId
                              ] && (
                                <div className="mb-4 rounded border border-orange-200 bg-orange-50 p-2">

                                  <div className="mb-2 px-1">

                                    <p className="text-xs font-bold text-orange-700">
                                      Reporting Service Preview
                                    </p>

                                    <p className="text-[9px] text-orange-600">
                                      This temporary preview is generated from the normalized evaluation reporting endpoint.
                                    </p>

                                  </div>


                                  <EtIndividualEvaluationReportPreview
                                    orgId={
                                      getIdValue(
                                        userOrgId
                                      )
                                    }
                                    trailId={
                                      getIdValue(
                                        trailId
                                      )
                                    }
                                    sessionId={
                                      getIdValue(
                                        sessionId
                                      )
                                    }
                                    challengeId={
                                      challengeId
                                    }
                                    userId={
                                      getIdValue(
                                        userId
                                      )
                                    }
                                    evaluationNumber={
                                      selectedSnapshot
                                        .evaluationNumber
                                    }
                                  />

                                </div>
                              )}

                              {/* 32b - Lumin and Spider Tabs for the Selected Evaluation */}
                              <div className="mb-3 flex items-center overflow-x-auto border-b border-gray-300">

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleEvaluationSnapshotViewChange(
                                      selectedSnapshot.snapshotKey,
                                      "lumin"
                                    )
                                  }
                                  className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                                    selectedSnapshotView === "lumin"
                                      ? "border-orange-600 text-orange-600"
                                      : "border-transparent text-gray-500 hover:text-orange-600"
                                  }`}
                                >
                                  Lumin View
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleEvaluationSnapshotViewChange(
                                      selectedSnapshot.snapshotKey,
                                      "spider"
                                    )
                                  }
                                  className={`shrink-0 border-b-2 px-4 py-2 text-[10px] font-semibold transition ${
                                    selectedSnapshotView === "spider"
                                      ? "border-orange-600 text-orange-600"
                                      : "border-transparent text-gray-500 hover:text-orange-600"
                                  }`}
                                >
                                  Spider View
                                </button>

                              </div>


                              {selectedSnapshotView === "lumin" ? (
                                <SelectedEvaluationLuminView
                                  challenge={
                                    challenge
                                  }
                                  snapshot={
                                    selectedSnapshot
                                  }
                                  groupedDimensions={
                                    groupedDimensions
                                  }
                                  selfUserData={
                                    selfUserData
                                  }
                                  showChallengeHeadDetails={
                                    showChallengeHeadDetails
                                  }
                                  showChallengeDimDetails={
                                    showChallengeDimDetails
                                  }
                                  showChallengeDimEvalDetails={
                                    showChallengeDimEvalDetails
                                  }
                                  setShowChallengeDimEvalDetails={
                                    setShowChallengeDimEvalDetails
                                  }
                                  handleToggleChallengeHeadDetails={
                                    handleToggleChallengeHeadDetails
                                  }
                                  handleToggleChallengeDimDetails={
                                    handleToggleChallengeDimDetails
                                  }
                                  handleToggleChallengeDimEvalDetails={
                                    handleToggleChallengeDimEvalDetails
                                  }
                                  hasEvaluatorDataForHead={
                                    hasEvaluatorDataForHead
                                  }

                                  // 21 Aug 2026
                                  hasTeamEvaluatorDataForHead={hasTeamEvaluatorDataForHead}


                                  renderScoreTile={
                                    renderScoreTile
                                  }
                                  getIdValue={
                                    getIdValue
                                  }
                                  getUsername={
                                    getUsername
                                  }
                                  getFirstName={
                                    getFirstName
                                  }


                                />
                              ) : (
                                <EvaluationSpiderView
                                  radarConfiguration={
                                    selectedRadarConfiguration
                                  }
                                />
                              )}

                            </div>
                          ) : null}

                        </div>

                      </div>

                    </div>
                  )}

                </div>
              )
            }
          )}

        </div>
      )}


      {/* 32 - One Self Evaluation Modal Instance */}
      <EtModalAddOrUpdateUserChallengeSelfEval
        isOpen={isModalOpen}
        onClose={
          handleCloseChallengeEvalModal
        }
        challengeId={activeChallengeId}
        userId={userId}
        userOrgId={userOrgId}
        trailId={trailId}
        sessionId={sessionId}
        cohortId={cohortId}
        triggeringUrl={triggeringUrl}
        mode={mode}
        existingEvalData={
          existingEvalData
        }
      />

    </div>
  )
}


// 33 - Evaluation Trend Carousel
// First Created - 02 Aug 2026
// Author - Atul Pandey
// Purpose - to display one separate spider profile card for each self evaluation in chronological order
// Important - each trend card uses the same exact evaluation number matching rule as the evaluation history card
const EvaluationTrendCarousel = ({
  snapshots,
  radarConfigurationBySnapshotKey,
  formatEvaluationDate,
}) => {

  const trendCarouselRef =
    useRef(null)


  const scrollTrendCarousel = (
    direction
  ) => {
    trendCarouselRef.current?.scrollBy({
      left:
        direction === "left"
          ? -560
          : 560,
      behavior: "smooth",
    })
  }


  return (
    <div className="mt-2">

      <div className="mb-2 flex justify-end gap-1">

        <button
          type="button"
          onClick={() =>
            scrollTrendCarousel("left")
          }
          className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
          aria-label="Scroll evaluation trend left"
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() =>
            scrollTrendCarousel("right")
          }
          className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600"
          aria-label="Scroll evaluation trend right"
        >
          <ArrowRightIcon className="h-4 w-4" />
        </button>

      </div>


      <div
        ref={trendCarouselRef}
        className="overflow-x-auto overscroll-x-contain pb-2 scroll-smooth"
      >

        <div className="flex min-w-max items-stretch gap-3">

          {snapshots.map(
            (snapshot) => {

              const radarConfiguration =
                radarConfigurationBySnapshotKey[
                  snapshot.snapshotKey
                ]

              return (
                <div
                  key={
                    `trend-${snapshot.snapshotKey}`
                  }
                  className="w-[420px] shrink-0 rounded border border-gray-200 bg-white p-4 sm:w-[520px] lg:w-[580px]"
                >

                  <div className="mb-2 flex items-start justify-between gap-2">

                    <div>

                      <p className="text-[11px] font-bold text-gray-700">
                        Evaluation {snapshot.evaluationNumber}
                      </p>

                      <p className="text-[9px] text-gray-500">
                        {formatEvaluationDate(
                          snapshot.selfEvaluation
                            ?.evaluatedAt ||
                          snapshot.selfEvaluation
                            ?.createdAt
                        )}
                      </p>

                    </div>

                    <span className="rounded-full bg-orange-50 px-2 py-1 text-[8px] font-semibold text-orange-600">
                      {1 +
                        snapshot.coachEvaluations.length +
                        snapshot.sponsorEvaluations.length}{" "}
                      evaluator
                      {1 +
                        snapshot.coachEvaluations.length +
                        snapshot.sponsorEvaluations.length ===
                      1
                        ? ""
                        : "s"}
                    </span>

                  </div>


                  <EvaluationSpiderView
                    radarConfiguration={
                      radarConfiguration
                    }
                    compact
                  />

                </div>
              )
            }
          )}

        </div>

      </div>

    </div>
  )
}


// 34 - Selected Evaluation Lumin View
// First Created - 03 Aug 2026
// Author - Atul Pandey
// Purpose - to display the Lumin matrix for the evaluation selected in the top level history tabs
// Important - Coach and Sponsor records are included only when their evaluationNumber
// exactly matches the selected self evaluationNumber.
const SelectedEvaluationLuminView = ({
  snapshot,
  groupedDimensions,
  selfUserData,
  showChallengeHeadDetails,
  showChallengeDimDetails,
  showChallengeDimEvalDetails,
  setShowChallengeDimEvalDetails,
  handleToggleChallengeHeadDetails,
  handleToggleChallengeDimDetails,
  handleToggleChallengeDimEvalDetails,
  hasEvaluatorDataForHead,
  hasTeamEvaluatorDataForHead,        // 21 Aug 2026
  renderScoreTile,
  getIdValue,
  getUsername,
  getFirstName,
}) => {

  const snapshotHeadStateKey =
    `${snapshot.snapshotKey}-heads`


  return (
    <div className="rounded bg-gray-200 p-1">

      <div className="m-1 flex items-center p-1">

        <button
          type="button"
          onClick={() =>
            handleToggleChallengeHeadDetails(
              snapshotHeadStateKey
            )
          }
          className="mr-2 flex h-6 w-6 items-center justify-center rounded-full hover:bg-white"
        >
          {showChallengeHeadDetails[
            snapshotHeadStateKey
          ] ? (
            <MinusIcon className="h-4 w-4 text-orange-600" />
          ) : (
            <PlusIcon className="h-4 w-4 text-orange-600" />
          )}
        </button>

        <div className="mr-2 flex h-7 w-7 items-center justify-center rounded-full hover:bg-white">
          <SunIcon className="h-6 w-6 text-orange-600" />
        </div>

        <div>

          <p className="font-bold text-gray-500">
            Dimensions
          </p>

          {/* 21 Aug 2026 */}

          <p className="text-[9px] text-gray-500">
            Eval {snapshot.evaluationNumber}: You,
            {" "}{snapshot.coachEvaluations.length}{" "}
            Coach,
            {" "}{snapshot.sponsorEvaluations.length}{" "}
            Sponsor and
            {" "}{snapshot.teamEvaluations.length}{" "}
            Team evaluation
            {snapshot.teamEvaluations.length === 1
              ? ""
              : "s"}
          </p>

          {/* <p className="text-[9px] text-gray-500">
            Eval {snapshot.evaluationNumber}: You,
            {" "}{snapshot.coachEvaluations.length}{" "}
            Coach and
            {" "}{snapshot.sponsorEvaluations.length}{" "}
            Sponsor
          </p> */}

        </div>

      </div>


      {showChallengeHeadDetails[
        snapshotHeadStateKey
      ] &&
        Object.entries(
          groupedDimensions
        ).map(
          ([headName, dimensions]) => {

            const challengeHeadKey =
              `${snapshot.snapshotKey}-${headName}`

            const evaluators = [
              ...(hasEvaluatorDataForHead(
                snapshot.selfEvaluation,
                dimensions,
                "userChallengeEvals"
              )
                ? [
                    {
                      id:
                        `self-${getIdValue(
                          snapshot.selfEvaluation?._id
                        )}`,
                      user:
                        selfUserData,
                      relation:
                        `You · Eval ${snapshot.evaluationNumber}`,
                      type: "self",
                      matchingEval:
                        snapshot.selfEvaluation,
                    },
                  ]
                : []),

              ...snapshot.coachEvaluations
                .filter(
                  (matchingCoachEval) =>
                    hasEvaluatorDataForHead(
                      matchingCoachEval,
                      dimensions,
                      "userChallengeCoachEvals"
                    )
                )
                .map(
                  (matchingCoachEval) => ({
                    id:
                      `coach-${getIdValue(
                        matchingCoachEval?.coachId
                      )}-${getIdValue(
                        matchingCoachEval?._id
                      )}`,
                    user:
                      matchingCoachEval?.coachId,
                    relation:
                      `Coach · Eval ${snapshot.evaluationNumber}`,
                    type: "coach",
                    matchingEval:
                      matchingCoachEval,
                  })
                ),

              ...snapshot.sponsorEvaluations
                .filter(
                  (matchingSponsorEval) =>
                    hasEvaluatorDataForHead(
                      matchingSponsorEval,
                      dimensions,
                      "userChallengeSponsorEvals"
                    )
                )
                .map(
                  (matchingSponsorEval) => ({
                    id:
                      `sponsor-${getIdValue(
                        matchingSponsorEval?.sponsorId
                      )}-${getIdValue(
                        matchingSponsorEval?._id
                      )}`,
                    user:
                      matchingSponsorEval?.sponsorId,
                    relation:
                      `Sponsor · Eval ${snapshot.evaluationNumber}`,
                    type: "sponsor",
                    matchingEval:
                      matchingSponsorEval,
                  })
                ),

              ...(hasTeamEvaluatorDataForHead(
                  snapshot.teamEvaluations,
                  dimensions
                )
                  ? [
                      {
                        id:
                          `team-${snapshot.snapshotKey}`,

                        user:
                          null,

                        relation:
                          `Team · Eval ${snapshot.evaluationNumber}`,

                        type:
                          "team",

                        matchingEvals:
                          snapshot.teamEvaluations,
                      },
                    ]
                  : []),
            ]


            return (
              <div
                key={
                  challengeHeadKey
                }
                className="m-1 rounded bg-white p-1"
              >

                <div className="flex items-center justify-between rounded bg-gray-50 p-2">

                  <div className="flex min-w-0 items-center">

                    <button
                      type="button"
                      onClick={() =>
                        handleToggleChallengeDimDetails(
                          challengeHeadKey
                        )
                      }
                      className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                    >
                      {showChallengeDimDetails[
                        challengeHeadKey
                      ] ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                      )}
                    </button>

                    <div className="min-w-0">

                      <p className="truncate font-bold">
                        {headName}
                      </p>

                      <p className="text-[9px] text-gray-500">
                        {dimensions.length}{" "}
                        dimension
                        {dimensions.length === 1
                          ? ""
                          : "s"}
                      </p>

                    </div>

                  </div>

                </div>


                {showChallengeDimDetails[
                  challengeHeadKey
                ] && (
                  <ChallengeEvaluationMatrix
                    challengeId={
                      snapshot.snapshotKey
                    }
                    dimensions={
                      dimensions
                    }
                    evaluators={
                      evaluators
                    }
                    showChallengeDimEvalDetails={
                      showChallengeDimEvalDetails
                    }
                    setShowChallengeDimEvalDetails={
                      setShowChallengeDimEvalDetails
                    }
                    handleToggleChallengeDimEvalDetails={
                      handleToggleChallengeDimEvalDetails
                    }
                    renderScoreTile={
                      renderScoreTile
                    }
                    getIdValue={
                      getIdValue
                    }
                    getDimensionEvaluation={
                      getDimensionEvaluation
                    }
                    getUsername={
                      getUsername
                    }
                    getFirstName={
                      getFirstName
                    }
                  />
                )}

              </div>
            )
          }
        )}

    </div>
  )
}


// 35 - Reusable Evaluation Spider View
// First Created - 02 Aug 2026
// Author - Atul Pandey
// Purpose - to render one exact evaluation number snapshot in the evaluation card and trend carousel
const EvaluationSpiderView = ({
  radarConfiguration,
  compact = false,
}) => {

  if (!radarConfiguration?.hasChartData) {
    return (
      <div className="rounded border border-orange-200 bg-orange-50 p-4">

        <p className="text-xs font-semibold text-orange-600">
          No evaluation scores are available for this snapshot.
        </p>

      </div>
    )
  }


  return (
    <div className="rounded bg-white p-2">

      {!compact && (
        <div className="mb-3">

          <p className="text-xs font-bold text-gray-700">
            Evaluation Dimension Profile
          </p>

          <p className="text-[10px] italic text-gray-500">
            Only evaluator records with this exact evaluation number are plotted.
          </p>

        </div>
      )}


      <div
        className={`w-full rounded bg-gray-50 p-1 ${
          compact
            ? "h-[360px] sm:h-[430px]"
            : "h-[340px] sm:h-[420px] lg:h-[480px] sm:p-2"
        }`}
      >

        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <RadarChart
            data={
              radarConfiguration.chartData
            }
            outerRadius={
              compact ? "68%" : "68%"
            }
            margin={{
              top: 30,
              right: 35,
              bottom: 30,
              left: 35,
            }}
          >

            <PolarGrid />

            <PolarAngleAxis
              dataKey="shortDimensionName"
              tick={{
                fill: "#4b5563",
                fontSize:
                  compact ? 8 : 9,
              }}
            />

            <PolarRadiusAxis
              angle={90}
              domain={[0, 5]}
              tickCount={6}
              tick={{
                fill: "#9ca3af",
                fontSize: 8,
              }}
            />

            <Tooltip
              content={
                <ChallengeRadarTooltip />
              }
            />

            <Legend
              wrapperStyle={{
                fontSize:
                  compact ? "8px" : "10px",
                paddingTop: "10px",
              }}
            />


            {radarConfiguration
              .evaluatorSeries
              .map(
                (
                  series,
                  seriesIndex
                ) => {

                  const seriesColor =
                    radarColors[
                      seriesIndex %
                        radarColors.length
                    ]

                  return (
                    <Radar
                      key={
                        series.dataKey
                      }
                      name={
                        series.label
                      }
                      dataKey={
                        series.dataKey
                      }
                      stroke={
                        seriesColor
                      }
                      fill={
                        seriesColor
                      }
                      fillOpacity={
                        seriesIndex === 0
                          ? 0.18
                          : 0.06
                      }
                      strokeWidth={
                        seriesIndex === 0
                          ? 3
                          : 2
                      }
                      connectNulls
                    />
                  )
                }
              )}

          </RadarChart>
        </ResponsiveContainer>

      </div>


      {!compact && (
        <div className="mt-2 rounded bg-gray-50 px-3 py-2">

          <p className="text-[9px] text-gray-500">
            Scores use the same 0 to 5 unlock scale as the Lumin tiles. Dimensions without an evaluation are left unplotted.
          </p>

        </div>
      )}

    </div>
  )
}

// 19 - Helper function to extract an evaluator score and comment for a dimension
// First Created - 01 Aug 2026
// Author - Atul Pandey
// Purpose - to provide one normalized evaluation object for self, coach and sponsor evaluators
const getDimensionEvaluation = (
  evaluator,
  dimensionId,
  getIdValue
) => {

  if (evaluator?.type === "self") {

    const evaluation =
      evaluator?.matchingEval
        ?.userChallengeEvals
        ?.find(
          (item) =>
            getIdValue(
              item?.userChallengeDimId
            ) === dimensionId
        )

    return {
      score:
        evaluation
          ?.userChallengeDimSelfScore,
      text:
        evaluation
          ?.userChallengeDimSelfEvalText,
    
    // 02 Aug 2026 - Use the evaluation creation date when the comment has not been updated
    createDate:
        evaluation
        ?.userChallengeDimSelfEvalDate,

      updateDate:
        evaluation
          ?.userChallengeDimSelfEvalTextUpdateDate,
      emptyMessage:
        "You have not evaluated this dimension yet.",
    }
  }


  if (evaluator?.type === "coach") {

    const evaluation =
      evaluator?.matchingEval
        ?.userChallengeCoachEvals
        ?.find(
          (item) =>
            getIdValue(
              item?.userChallengeDimId
            ) === dimensionId
        )

    return {
      score:
        evaluation
          ?.userChallengeDimCoachScore,
      text:
        evaluation
          ?.userChallengeDimCoachEvalText,
        
      // 02 Aug 2026 - Use the evaluation creation date when the comment has not been updated
      createDate:
         evaluation
          ?.userChallengeDimCoachEvalDate,
        
      updateDate:
        evaluation
          ?.userChallengeDimCoachEvalTextUpdateDate,
      emptyMessage:
        "Coach has not evaluated this dimension yet.",
    }
  }

  // 21 Aug 2026
  if (evaluator?.type === "team") {

    const teamAggregate =
      getTeamDimensionAggregate(
        evaluator?.matchingEvals || [],
        dimensionId
      )

    return {
      score:
        teamAggregate.score,

      text:
        teamAggregate
          .evaluationCount > 0
          ? `${teamAggregate.evaluationCount} Team evaluation${
              teamAggregate.evaluationCount === 1
                ? ""
                : "s"
            } contributed to this score.${
              teamAggregate.commentCount > 0
                ? ` ${teamAggregate.commentCount} included comments.`
                : ""
            }`
          : "",

      createDate:
        null,

      updateDate:
        null,

      emptyMessage:
        "Team has not evaluated this dimension yet.",
    }
  }


  const evaluation =
    evaluator?.matchingEval
      ?.userChallengeSponsorEvals
      ?.find(
        (item) =>
          getIdValue(
            item?.userChallengeDimId
          ) === dimensionId
      )

  return {
    score:
      evaluation
        ?.userChallengeDimSponsorScore,
    text:
      evaluation
        ?.userChallengeDimSponsorEvalText,

    // 02 Aug 2026 - Use the evaluation creation date when the comment has not been updated
    createDate:
      evaluation
        ?.userChallengeDimSponsorEvalDate,

    updateDate:
      evaluation
        ?.userChallengeDimSponsorEvalTextUpdateDate,
    emptyMessage:
      "Sponsor has not evaluated this dimension yet.",
  }
}


// 20 - Responsive Challenge Evaluation Matrix
// First Created - 01 Aug 2026
// Author - Atul Pandey
// Purpose - to display challenge dimensions and evaluator scores within one aligned responsive matrix
// Updated - 01 Aug 2026 - changed the dimension and evaluator columns from independent lists to one CSS grid
// Updated - 01 Aug 2026 - added a sticky challenge dimension column
// Updated - 01 Aug 2026 - added a horizontally scrollable evaluator matrix
// Updated - 01 Aug 2026 - added evaluator specific inline comment display
// Updated - 01 Aug 2026 - added show and hide all comments control by dimension
// Updated - 01 Aug 2026 - added stacked mobile dimension cards with evaluator specific comments
const ChallengeEvaluationMatrix = ({
  challengeId,
  dimensions,
  evaluators,
  showChallengeDimEvalDetails,
  setShowChallengeDimEvalDetails,
  handleToggleChallengeDimEvalDetails,
  renderScoreTile,
  getIdValue,
  getDimensionEvaluation,
  getUsername,
  getFirstName,
}) => {

  // 20a - Reference for the complete desktop evaluation matrix carousel
  const evaluatorMatrixRef =
    useRef(null)


  // 20b - Scroll the complete evaluator matrix left or right
  const scrollEvaluatorMatrix = (
    direction
  ) => {
    evaluatorMatrixRef.current?.scrollBy({
      left:
        direction === "left"
          ? -320
          : 320,
      behavior: "smooth",
    })
  }


  // 20c - Create a unique comment state key for an evaluator and dimension
  const getEvaluatorCommentKey = (
    dimensionId,
    evaluatorId
  ) => {
    return `${challengeId}-${dimensionId}-${evaluatorId}`
  }


  // 20d - Toggle all evaluator comments within one challenge dimension
  const handleToggleAllDimensionComments = (
    dimensionId
  ) => {
    const evaluatorCommentKeys =
      evaluators.map((evaluator) =>
        getEvaluatorCommentKey(
          dimensionId,
          evaluator.id
        )
      )

    const shouldOpenAll =
      evaluatorCommentKeys.some(
        (commentKey) =>
          !showChallengeDimEvalDetails[
            commentKey
          ]
      )

    setShowChallengeDimEvalDetails(
      (previous) => {

        const updatedState = {
          ...previous,
        }

        evaluatorCommentKeys.forEach(
          (commentKey) => {
            updatedState[commentKey] =
              shouldOpenAll
          }
        )

        return updatedState
      }
    )
  }


  // 20e - Determine whether any evaluator comment is open for a dimension
  const isAnyDimensionCommentOpen = (
    dimensionId
  ) => {
    return evaluators.some(
      (evaluator) => {

        const commentKey =
          getEvaluatorCommentKey(
            dimensionId,
            evaluator.id
          )

        return Boolean(
          showChallengeDimEvalDetails[
            commentKey
          ]
        )
      }
    )
  }


  // 20f - Build the desktop matrix grid columns
  // Updated - 01 Aug 2026 - increased the challenge dimension column width
  // Updated - 01 Aug 2026 - increased evaluator column width to provide more room for evaluation comments
  const evaluatorMatrixGridStyle = {
    gridTemplateColumns:
        `minmax(380px, 440px) repeat(${Math.max(
        evaluators.length,
        1
        )}, minmax(230px, 250px))`,
  }


  if (!evaluators.length) {
    return (
      <div className="m-1 rounded border border-orange-200 bg-orange-50 p-3">

        <p className="text-[10px] text-orange-600">
          No user, coach or sponsor
          evaluations are available for this
          challenge head yet.
        </p>

      </div>
    )
  }


  return (
    <div className="mt-2">

      {/* 20g - Desktop and Tablet Evaluation Matrix */}
      <div className="hidden md:block">

        {/* 20h - Evaluation Matrix Navigation Header */}
        <div className="mb-2 flex items-center justify-between rounded bg-gray-50 px-3 py-2">

          <div>

            <p className="text-[10px] font-bold text-gray-700">
              Dimension Evaluations
            </p>

            <p className="text-[9px] text-gray-500">
              Scroll horizontally to compare
              evaluations and comments
            </p>

          </div>


          <div className="flex items-center gap-1">

            <button
              type="button"
              onClick={() =>
                scrollEvaluatorMatrix(
                  "left"
                )
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition hover:border-orange-300 hover:text-orange-600"
              aria-label="Scroll evaluator matrix left"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>


            <button
              type="button"
              onClick={() =>
                scrollEvaluatorMatrix(
                  "right"
                )
              }
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition hover:border-orange-300 hover:text-orange-600"
              aria-label="Scroll evaluator matrix right"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>

          </div>

        </div>


        {/* 20i - Horizontally Scrollable Complete Evaluation Matrix */}
        <div
          ref={evaluatorMatrixRef}
          className="overflow-x-auto overscroll-x-contain rounded scroll-smooth"
        >

          <div
            // className="grid min-w-max items-stretch gap-px bg-gray-200"
            className="grid min-w-max items-stretch gap-2 bg-white p-1"
            style={
              evaluatorMatrixGridStyle
            }
          >

            {/* 20j - Fixed Challenge Dimension Header */}
            {/* <div className="sticky left-0 z-30 flex min-h-[122px] items-center border-r border-gray-200 bg-gray-100 p-4 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]"> */}
            <div className="sticky left-0 z-30 flex min-h-[132px] items-center rounded border border-gray-200 bg-gray-100 p-5 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]">

              <div>

                <p className="text-xs font-bold text-gray-700">
                Challenge Dimension
                </p>

                <p className="mt-2 max-w-[320px] text-[10px] leading-5 text-gray-500">
                Dimension details and evaluator specific
                comments
                </p>

                {/* <p className="text-[10px] font-bold text-gray-700">
                  Challenge Dimension
                </p>

                <p className="mt-1 text-[9px] leading-4 text-gray-500">
                  Dimension details and
                  evaluator specific comments
                </p> */}

              </div>

            </div>


            {/* 20k - Evaluator Header Cells */}
            {evaluators.map(
              (evaluator) => (
                <div
                  key={
                    `header-${evaluator.id}`
                  }
                  className="flex min-h-[132px] items-center justify-center rounded border border-gray-200 bg-gray-100 px-5 py-3"
                //   className="flex min-h-[122px] items-center justify-center bg-gray-100 p-2"
                >

                  <EvaluatorHeader
                    evaluator={
                      evaluator
                    }
                    getUsername={
                      getUsername
                    }
                    getFirstName={
                      getFirstName
                    }
                  />

                </div>
              )
            )}


            {/* 20l - Render each dimension and corresponding evaluator cells within the same grid row */}
            {dimensions.map(
              (
                dimension,
                dimensionIndex
              ) => {

                const dimensionId =
                  getIdValue(
                    dimension?.challengeDimId
                  )

                const hasOpenComments =
                  isAnyDimensionCommentOpen(
                    dimensionId
                  )


                return (
                  <React.Fragment
                    key={
                      `${challengeId}-${dimensionId}`
                    }
                  >

                    {/* 20m - Sticky Challenge Dimension Cell */}
                    {/* <div className="sticky left-0 z-20 flex min-h-[168px] items-start border-r border-gray-200 bg-white p-3 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]"> */}
                    <div className="sticky left-0 z-20 flex min-h-[190px] items-start rounded border border-gray-200 bg-white p-5 shadow-[4px_0_8px_-6px_rgba(0,0,0,0.35)]">

                      <div className="flex min-w-0 flex-1 items-start">

                        {/* <p className="mr-3 shrink-0 text-4xl font-bold text-gray-300"> */}
                        <p className="mr-4 shrink-0 text-4xl font-bold text-gray-300">
                          {dimensionIndex + 1}.
                        </p>


                        <div className="min-w-0 flex-1">

                          {/* <p className="text-[11px] font-bold leading-5 text-gray-700"> */}
                          <p className="text-xs font-bold leading-5 text-gray-700">
                            {
                              dimension
                                ?.challengeDimId
                                ?.challengeDimensionName
                            }
                          </p>

                          {/* <p className="mt-1 text-[9px] italic leading-4 text-gray-500"> */}
                          <p className="mt-2 max-w-[315px] text-[10px] italic leading-5 text-gray-500">
                            {
                              dimension
                                ?.challengeDimId
                                ?.challengeDimensionDesc
                            }
                          </p>


                          {/* 20n - Show or Hide All Evaluator Comments for the Dimension */}
                          <button
                            type="button"
                            onClick={() =>
                              handleToggleAllDimensionComments(
                                dimensionId
                              )
                            }
                            className="mt-3 flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[9px] font-semibold text-orange-600 transition hover:bg-orange-100"
                          >
                            {hasOpenComments ? (
                              <MinusIcon className="mr-1 h-3 w-3" />
                            ) : (
                              <PlusIcon className="mr-1 h-3 w-3" />
                            )}

                            {hasOpenComments
                              ? "Hide all comments"
                              : "Show all comments"}
                          </button>

                        </div>

                      </div>

                    </div>


                    {/* 20o - Evaluator Score and Comment Cells */}
                    {evaluators.map(
                      (evaluator) => {

                        const evaluation =
                          getDimensionEvaluation(
                            evaluator,
                            dimensionId,
                            getIdValue
                          )

                        const evaluatorCommentKey =
                          getEvaluatorCommentKey(
                            dimensionId,
                            evaluator.id
                          )

                        const showEvaluatorComment =
                          Boolean(
                            showChallengeDimEvalDetails[
                              evaluatorCommentKey
                            ]
                          )


                        return (
                          <div
                            key={
                              evaluatorCommentKey
                            }
                            className="flex min-h-[190px] min-w-0 flex-col items-center rounded border border-gray-200 bg-white px-5 py-4"
                            // className="flex min-h-[168px] flex-col items-center bg-white p-3"
                          >

                            {/* 20p - Evaluator Score */}
                            {/* <div className="flex justify-center"> */}
                            <div className="flex w-full justify-center rounded bg-gray-50 py-3">

                              {renderScoreTile(
                                evaluation.score
                              )}

                            </div>


                            {/* 20q - Evaluator Specific Comment Toggle */}
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleChallengeDimEvalDetails(
                                  evaluatorCommentKey
                                )
                              }
                            //   className={`mt-3 flex items-center rounded-full border px-2 py-1 text-[8px] font-semibold transition ${
                            className={`mt-4 flex min-w-[100px] items-center justify-center rounded-full border px-4 py-1.5 text-[9px] font-semibold transition ${
                                showEvaluatorComment
                                  ? "border-orange-300 bg-orange-50 text-orange-600"
                                  : "border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600"
                              }`}
                            >
                              {showEvaluatorComment ? (
                                <MinusIcon className="mr-1 h-3 w-3" />
                              ) : (
                                <PlusIcon className="mr-1 h-3 w-3" />
                              )}

                              Comments
                            </button>


                            {/* 20r - Evaluator Specific Inline Comment */}
                            {showEvaluatorComment && (
                              <InlineEvaluatorComment
                                evaluationText={
                                  evaluation.text
                                }
                                // 02 Aug 2026
                                evaluationCreateDate={
                                    evaluation.createDate
                                }
                                evaluationUpdateDate={
                                  evaluation.updateDate
                                }
                                emptyMessage={
                                  evaluation.emptyMessage
                                }
                              />
                            )}

                          </div>
                        )
                      }
                    )}

                  </React.Fragment>
                )
              }
            )}

          </div>

        </div>

      </div>


      {/* 20s - Mobile Evaluation Cards */}
      <div className="space-y-3 md:hidden">

        {dimensions.map(
          (
            dimension,
            dimensionIndex
          ) => {

            const dimensionId =
              getIdValue(
                dimension?.challengeDimId
              )

            const hasOpenComments =
              isAnyDimensionCommentOpen(
                dimensionId
              )


            return (
              <div
                key={
                  `${challengeId}-${dimensionId}`
                }
                className="rounded bg-gray-50 p-3"
              >

                {/* 20t - Mobile Dimension Header */}
                <div className="flex items-start">

                  <p className="mr-2 shrink-0 text-3xl font-bold text-gray-300">
                    {dimensionIndex + 1}.
                  </p>


                  <div className="min-w-0 flex-1">

                    <p className="text-[11px] font-bold leading-5 text-gray-700">
                      {
                        dimension
                          ?.challengeDimId
                          ?.challengeDimensionName
                      }
                    </p>

                    <p className="mt-1 text-[9px] italic leading-4 text-gray-500">
                      {
                        dimension
                          ?.challengeDimId
                          ?.challengeDimensionDesc
                      }
                    </p>


                    <button
                      type="button"
                      onClick={() =>
                        handleToggleAllDimensionComments(
                          dimensionId
                        )
                      }
                      className="mt-2 flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-[9px] font-semibold text-orange-600"
                    >
                      {hasOpenComments ? (
                        <MinusIcon className="mr-1 h-3 w-3" />
                      ) : (
                        <PlusIcon className="mr-1 h-3 w-3" />
                      )}

                      {hasOpenComments
                        ? "Hide all comments"
                        : "Show all comments"}
                    </button>

                  </div>

                </div>


                {/* 20u - Mobile Evaluator Carousel */}
                <div className="mt-3 overflow-x-auto overscroll-x-contain pb-2">

                  <div className="flex min-w-max items-stretch gap-2">

                    {evaluators.map(
                      (evaluator) => {

                        const evaluation =
                          getDimensionEvaluation(
                            evaluator,
                            dimensionId,
                            getIdValue
                          )

                        const evaluatorCommentKey =
                          getEvaluatorCommentKey(
                            dimensionId,
                            evaluator.id
                          )

                        const showEvaluatorComment =
                          Boolean(
                            showChallengeDimEvalDetails[
                              evaluatorCommentKey
                            ]
                          )


                        return (
                          <div
                            key={
                              evaluatorCommentKey
                            }
                            className="flex w-[210px] shrink-0 flex-col rounded border border-gray-200 bg-white p-4"
                            // className="flex w-[170px] shrink-0 flex-col rounded border border-gray-200 bg-white p-3"
                          >

                            <EvaluatorHeader
                              evaluator={
                                evaluator
                              }
                              getUsername={
                                getUsername
                              }
                              getFirstName={
                                getFirstName
                              }
                              compact
                            />


                            <div className="mt-3 flex justify-center">

                              {renderScoreTile(
                                evaluation.score
                              )}

                            </div>


                            <button
                              type="button"
                              onClick={() =>
                                handleToggleChallengeDimEvalDetails(
                                  evaluatorCommentKey
                                )
                              }
                              className={`mt-3 flex items-center justify-center rounded-full border px-2 py-1 text-[8px] font-semibold ${
                                showEvaluatorComment
                                  ? "border-orange-300 bg-orange-50 text-orange-600"
                                  : "border-gray-200 bg-gray-50 text-gray-500"
                              }`}
                            >
                              {showEvaluatorComment ? (
                                <MinusIcon className="mr-1 h-3 w-3" />
                              ) : (
                                <PlusIcon className="mr-1 h-3 w-3" />
                              )}

                              Comments
                            </button>


                            {showEvaluatorComment && (
                              <InlineEvaluatorComment
                                evaluationText={
                                  evaluation.text
                                }
                                // 02 Aug 2026
                                evaluationCreateDate={
                                    evaluation.createDate
                                }
                                evaluationUpdateDate={
                                  evaluation.updateDate
                                }
                                emptyMessage={
                                  evaluation.emptyMessage
                                }
                                mobile
                              />
                            )}

                          </div>
                        )
                      }
                    )}

                  </div>

                </div>

              </div>
            )
          }
        )}

      </div>

    </div>
  )
}


// 21 - Evaluator Header Component
// First Created - 28 Aug 2024
// Author - Atul Pandey
// Purpose - to display the user, coach or sponsor profile picture and relationship
// Updated - 01 Aug 2026 - refined as a reusable evaluator matrix header
// Updated - 01 Aug 2026 - added compact rendering for mobile evaluator cards
const EvaluatorHeader = ({
  evaluator,
  getUsername,
  getFirstName,
  compact = false,
}) => {

  // 21 Aug 2026
  const isTeamEvaluator =
    evaluator?.type === "team"

  const username =
    isTeamEvaluator
      ? ""
      : getUsername(
          evaluator?.user
        )

  // const username =
  //   getUsername(evaluator?.user)

  const {
    data: userPic,
    isFetching: isFetchingUserPic,
  } = useGetUserPicQuery(
    username,
    {
      skip: !username,
    }
  )


  return (
    <div className="flex w-full min-w-0 flex-col items-center justify-center">

      <p className="w-full truncate text-center text-[8px] font-semibold text-orange-600">
        {evaluator?.relation}
      </p>


      <div
        className={`mt-1 flex items-center justify-center rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow ${
          compact
            ? "h-9 w-9"
            : "h-11 w-11"
        }`}
      >

        { isTeamEvaluator ? (

          <UserGroupIcon
            className={
              compact
                ? "h-6 w-6 text-white"
                : "h-7 w-7 text-white"
            }
          />

        ) : isFetchingUserPic ? (
          <p className="text-[7px] text-white">
            ...
          </p>
        ) : userPic ? (
          <img
            src={userPic}
            alt={getFirstName(
              evaluator?.user
            )}
            className={`rounded-full object-cover ${
              compact
                ? "h-8 w-8"
                : "h-10 w-10"
            }`}
          />
        ) : (
          <img
            src={userProfileIcon2}
            alt={getFirstName(
              evaluator?.user
            )}
            className={`p-1 ${
              compact
                ? "h-8 w-8"
                : "h-10 w-10"
            }`}
          />
        )}

      </div>


      <p className="mt-1 w-full truncate text-center text-[9px] font-medium text-gray-600">
        {isTeamEvaluator
          ? "Team"
          : getFirstName(
              evaluator?.user
        )}
      </p>

    </div>
  )
}


// 22 - Inline Evaluator Comment Component

const InlineEvaluatorComment = ({
  evaluationText,
  evaluationCreateDate, // 02 Aug 2026
  evaluationUpdateDate,
  emptyMessage,
  mobile = false,
}) => {

    // First Created - 01 Aug 2026
    // Author - Atul Pandey
    // Purpose - to display the evaluation comment directly below the corresponding evaluator score
    // Updated - 01 Aug 2026 - added evaluator specific empty state and last updated date
    // Updated - 01 Aug 2026 - added compact mobile rendering
    // Updated - 01 Aug 2026 - increased comment width, padding and text spacing for desktop evaluation columns
    // Updated - 02 Aug 2026 - refined for newly created comment where no update date is present yet

  const hasEvaluationText =
    evaluationText &&
    evaluationText !== "N/A"

  // 02 Aug 2026 - Show the creation date when the evaluation comment has not been updated
  const evaluationDisplayDate =
    evaluationUpdateDate ||
    evaluationCreateDate

  const evaluationDateLabel =
    evaluationUpdateDate
      ? "Updated"
      : "Created"


  return (
    <div
      className={`mt-4 w-full min-w-0 rounded border border-gray-200 bg-gray-50 ${
        mobile
          ? "p-3"
          : "p-4"
      }`}
    >

      <p className="text-[8px] font-bold uppercase tracking-wide text-gray-500">
        Evaluation Comment
      </p>


      <p
        className={`mt-2 whitespace-normal break-words text-[10px] leading-5 ${
          hasEvaluationText
            ? "text-gray-700"
            : "text-orange-600"
        }`}
      >
        {hasEvaluationText
          ? evaluationText
          : emptyMessage}
      </p>


      <div className="mt-3 border-t border-gray-200 pt-3">

        <div className="flex flex-wrap items-center gap-y-1">

          <ClockIcon className="mr-1 h-3.5 w-3.5 shrink-0 text-lime-600" />

          {/* 02 Aug 2026 */}
          <p className="mr-1 text-[8px] font-semibold text-gray-500">
            {evaluationDateLabel}
            </p>

          <p className="text-[8px] italic text-gray-400">
            {evaluationDisplayDate
                ? getTimeSince(
                    evaluationDisplayDate
                )
                : ""}
          </p>

          {/* <p className="mr-1 text-[8px] font-semibold text-gray-500">
            Updated
          </p>

          <p className="text-[8px] italic text-gray-400">
            {evaluationUpdateDate &&
            evaluationUpdateDate !==
              "N/A"
              ? getTimeSince(
                  evaluationUpdateDate
                )
              : "Not updated"}
          </p> */}

        </div>

      </div>

    </div>
  )
}


// 23 - Spider Chart Tooltip Component
// First Created - 30 Jul 2026
// Author - Atul Pandey
// Purpose - to display evaluator scores for the selected challenge dimension within the spider chart
// Updated - 01 Aug 2026 - extracted from the Session Challenges component for readability and reuse
const ChallengeRadarTooltip = ({
  active,
  payload,
}) => {

  if (
    !active ||
    !payload?.length
  ) {
    return null
  }

  const chartRow =
    payload[0]?.payload


  return (
    <div className="max-w-72 rounded border border-gray-200 bg-white p-3 shadow-lg">

      <p className="text-xs font-bold text-gray-700">
        {chartRow?.dimensionName}
      </p>

      <p className="mb-2 text-[9px] text-gray-400">
        {chartRow?.headName}
      </p>


      <div className="space-y-1">

        {payload
          .filter(
            (item) =>
              item?.value !== null &&
              item?.value !== undefined
          )
          .map((item) => (
            <div
            //   key={item.dataKey}
            key={`${item.dataKey}-${item.name}`}
              className="flex items-center justify-between gap-4"
            >

              <p className="text-[10px] text-gray-600">
                {item.name}
              </p>

              <p className="text-[10px] font-bold text-gray-800">
                {item.value}
              </p>

            </div>
          ))}

      </div>

    </div>
  )
}


export default EtUserSessionChallengesSection